<!-- TextDisplayer.vue — v2 (refatorado; continua sendo usado pelo RightArea.vue) -->
<!-- Mudanças sobre a v1:                                                                  -->
<!--  • Os cards de contagem foram movidos para o CABEÇALHO, à esquerda dos botões de      -->
<!--    ação, como chip reduzida (não aumenta a altura do cabeçalho). Hoje é UMA chip, a    -->
<!--    do total, que abre "Minhas notas" e "Notas de outros" num dropdown ao clique.       -->
<!--  • Metadados reorganizados abaixo do cabeçalho:                                        -->
<!--      nome da atividade → card "Texto" agrupando título, subtítulo, temas e campos.     -->
<!--  • Categoria e assuntos continuam linkados aos respectivos cards.                      -->
<!--  • TextWithNotes.vue NÃO é tocado; rolagem geral mantém .text-scroll-container.       -->

<script setup>
import {
  ref,
  provide,
  onMounted,
  onUnmounted,
  computed,
  nextTick,
  watch,
} from "vue";

import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import {
  PLANNED_PROGRESS_ACTION,
  ACTIVITY_PROGRESS_MENU_OPTIONS,
  progressFullLabel,
  progressColorClass,
  progressMenuColorClass,
  progressIcon,
  isSameProgress,
  activityPlanningProgress,
} from "@/utils/activityProgress";
import { formatDateTimeForInput } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import { catalogChips, catalogLabel, sourceChip } from "@/utils/textCatalog";
import { coverStyle, coverTombo } from "@/utils/textCover";
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";
import api from "@/services/api";

import AddNoteForm from "../Note/AddNoteForm.vue";
import AddAiNoteForm from "../Note/AddAiNoteForm.vue";
import TextWithNotes from "./TextWithNotes.vue";
import TextWithNotesHtml from "./TextWithNotesHtml.vue";
import ConvertTextToHtmlModal from "./ConvertTextToHtmlModal.vue";
import RichTextEditor from "../Generics/RichTextEditor.vue";
import CustomTextarea from "../Generics/CustomTextarea.vue";
import { compareNotesByTextOrder } from "@/utils/noteOrdering";
import { toPlainText } from "@/utils/htmlProjection";
import { RUNNING_HEAD_CLASS } from "@/utils/tiptapEditorConfig";
import NoteCardModal from "../Note/NoteCardModal.vue";
import TextCardModal from "@/components/Text/TextCardModal.vue";
import EditTextForm from "@/components/Text/EditTextForm.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";
import AuthorCardModal from "@/components/Author/AuthorCardModal.vue";
import ActivityCardModal from "@/components/Activity/ActivityCardModal.vue";
import EditActivityForm from "@/components/Activity/EditActivityForm.vue";
import SeriesSwitcher from "@/components/Activity/SeriesSwitcher.vue";
import UserCardModal from "@/components/User/UserCardModal.vue";
import ExcerptMultiSelectMiniModal from "../Note/ExcerptMultiSelectMiniModal.vue";
import PrintNotes from "../Note/PrintNotes.vue";
// ── NOVO: DeleteNoteForm para apagar via menu de contexto do TextWithNotes ───
import DeleteNoteForm from "@/components/Note/DeleteNoteForm.vue";
// ✅ cards de categoria e assunto (como no TextReader)
import CategoryCardModal from "@/components/Category/CategoryCardModal.vue";
import TopicCardModal from "@/components/Topic/TopicCardModal.vue";

import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import { useTextStore } from "@/stores/useTextStore";
import { useAuthorStore } from "@/stores/useAuthorStore";
import { useCategoryStore } from "@/stores/useCategoryStore";
import { useTopicStore } from "@/stores/useTopicStore";
import { buildCoverFromFile } from "@/utils/coverImage";
import AddAuthorForm from "@/components/Author/AddAuthorForm.vue";
import AddCategoryForm from "@/components/Category/AddCategoryForm.vue";
import AddTopicForm from "@/components/Topic/AddTopicForm.vue";
import { useUserStore } from "@/stores/useUserStore";
import { useNoteStore } from "@/stores/useNoteStore";
import { useActivityStore } from "@/stores/useActivityStore";

const router = useRouter();

const activityStore = useActivityStore();
const noteStore = useNoteStore();
const userStore = useUserStore();
const textStore = useTextStore();
const authorStore = useAuthorStore();
const categoryStore = useCategoryStore();
const topicStore = useTopicStore();

const {
  isExponentMode: hasExponent,
  isHighlightsMode: hasHighlights,
  isCreatorsStackMode: hasCreatorsStack,
  isPageMarksMode: hasPageMarks,
  multiSelectedExcerpts,
} = storeToRefs(noteStore);

const isMultiSelectMode = ref(false);

// Qual renderizador montar. O padrão é texto puro de propósito: enquanto o campo
// não vier da API — ou vier vazio, em texto ainda não convertido — o comportamento
// tem de ser o de hoje.
const isPlaintextContent = computed(
  () => (textStore.selectedText?.contentFormat ?? "plaintext") !== "html"
);

const showConvertModal = ref(false);

// ── Edição do conteúdo na própria área de leitura ───────────────────────────
// Mesma regra do TextCardModal: Sys, ou quem criou o texto.
const canEditContent = computed(() => {
  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();
  if (!logged) return false;
  if (logged === "sys") return true;
  return (
    textStore.selectedText?.createdByUserId === userStore.currentUser?.userId
  );
});

// ── Edição dos metadados no próprio painel ───────────────────────────────────
// Mesma lógica dos lápis do NoteList.vue: o lápis de cada campo não abre um
// editor daquele campo, e sim transforma o painel em formulário com o cursor no
// campo escolhido. Gravar é atômico porque o PUT do texto é substituição — ver
// `updateTextFields` na store.
//
// Quem pode: o backend aceita o criador, o Sys e quem tem "Editar" explícito no
// texto. `canEditContent` já cobre os dois primeiros; o terceiro sai das
// permissões efetivas que o próprio DTO do texto devolve.
const { canEditText: memberCanEditText, canCreateNoteInActivity } =
  useMemberRestrictions();

// O vínculo permite anotar NESTA série? Governa a seleção de trecho (duplo
// clique e arrasto) e o interruptor de nota rápida — sem isso a pessoa
// selecionava, o formulário abria e só o backend recusava, no fim do caminho.
const memberCanCreateNote = computed(() =>
  canCreateNoteInActivity(activityStore.selectedActivity?.createdByUserId)
);

const blockedByMembershipHint =
  "Você só pode anotar em atividades preparadas pelo titular ou pelo círculo dele.";

const canEditMeta = computed(() => {
  const text = textStore.selectedText;
  if (!text || !memberCanEditText.value) return false;
  if (canEditContent.value) return true;
  return (text.userPermissions ?? []).some(
    (perm) => perm.permissionName?.toLowerCase() === "editar"
  );
});

const isEditingMeta = ref(false);

// Rascunho de uma edição fechada pelo Esc, junto do texto a que pertence.
// Guardado num só lugar porque o painel edita um texto de cada vez.
const pendingMetaDraft = ref(null);

const metaDraft = ref(null);
const isSavingMeta = ref(false);
const metaError = ref("");

// Listas de escolha. Carregadas na primeira edição, e não na montagem: quem só
// lê o texto não precisa do catálogo inteiro de autores, gêneros e assuntos.
const authorOptions = ref([]);
const categoryOptions = ref([]);
const topicOptions = ref([]);
const isLoadingMetaOptions = ref(false);

const metaRefs = {};
function setMetaRef(field) {
  return (el) => {
    metaRefs[field] = el;
  };
}

async function loadMetaOptions() {
  if (authorOptions.value.length || isLoadingMetaOptions.value) return;
  isLoadingMetaOptions.value = true;
  try {
    await Promise.all([
      authorStore.fetchAuthors(),
      categoryStore.fetchCategories(),
      topicStore.fetchTopics(),
    ]);
    authorOptions.value = (authorStore.authors || []).map((a) => ({
      label: a.authorName,
      value: a.authorId,
    }));
    categoryOptions.value = (categoryStore.categories || []).map((c) => ({
      label: c.categoryName,
      value: c.categoryId,
    }));
    topicOptions.value = (topicStore.topics || []).map((t) => ({
      label: t.topicName,
      value: t.topicId,
    }));
  } catch (error) {
    console.warn("Falha ao carregar as listas para edição do texto:", error);
  } finally {
    isLoadingMetaOptions.value = false;
  }
}

async function startMetaEdit(field) {
  if (!canEditMeta.value) return;

  const text = textStore.selectedText;
  if (!text) return;

  metaError.value = "";

  // O rascunho sai do que está na tela: o que se vê é o que se edita. Quem
  // garante que o resto do texto não se perde é a releitura na hora de GRAVAR.
  // Rascunho pendente vence o que está gravado: se o painel foi fechado por Esc,
  // o que o usuário escreveu é mais recente, e reconstruir a partir do texto
  // apagaria o trabalho dele em silêncio.
  const pending =
    pendingMetaDraft.value?.textId === text.textId
      ? pendingMetaDraft.value.draft
      : null;

  metaDraft.value = pending ?? {
    title: text.title ?? "",
    subtitle: text.subtitle ?? "",
    source: text.source ?? "",
    author: text.authorId
      ? { label: text.authorName, value: text.authorId }
      : null,
    category: text.categoryId
      ? { label: text.categoryName, value: text.categoryId }
      : null,
    topics: (text.topicNames ?? []).map((label, index) => ({
      label,
      value: text.topicIds?.[index] ?? null,
    })),
  };

  // A capa só é zerada quando NÃO há rascunho pendente: a imagem escolhida e
  // ainda não enviada faz parte do que o Esc preserva. Sem rascunho, começar
  // intocada é o que impede uma escolha descartada numa edição anterior de
  // voltar sozinha na seguinte.
  if (!pending) resetCoverEdit();

  isEditingMeta.value = true;
  loadMetaOptions();

  await nextTick();
  // Autor, categoria e assuntos são seletores: abrem sem foco, para a lista não
  // subir por cima do resto do formulário assim que ele aparece. Nos campos de
  // texto o foco é ganho — lá ele deixa o cursor pronto sem cobrir nada.
  //
  // O id é a segunda via do ref: `setMetaRef` devolve uma função nova a cada
  // render, e o Vue zera a referência antiga antes de gravar a nova — bastava o
  // formulário tornar a renderizar (as listas chegando, por exemplo) no intervalo
  // para o lápis abrir o campo sem cursor nenhum. O id não depende desse ciclo.
  if (["title", "subtitle", "source"].includes(field)) {
    const held = metaRefs[field];
    focusMetaFieldEnd(
      held?.isConnected ? held : document.getElementById(`meta-${field}`)
    );
  }
}

// Foco no FIM do conteúdo, e com o fim à vista: focar um campo não move o cursor
// nem rola o que já está escrito, e um título longo abriria mostrando o começo,
// com o cursor num lugar que o usuário não vê.
function focusMetaFieldEnd(el) {
  if (!el?.focus) return;
  el.focus();
  const end = el.value?.length ?? 0;
  el.setSelectionRange?.(end, end);
  el.scrollLeft = el.scrollWidth;
}

// Desistir de verdade: o rascunho vai embora junto. É o botão "Cancelar".
function cancelMetaEdit() {
  pendingMetaDraft.value = null;
  closeMetaEdit();
  resetCoverEdit();
}

/**
 * Esc fecha o painel de metadados guardando o rascunho.
 *
 * Ouvinte na JANELA e em fase de CAPTURA, pelo mesmo motivo do editor de
 * conteúdo: um `@keydown.esc` no formulário só responde com o foco dentro dele,
 * e o painel pode estar aberto sem foco em campo nenhum — pelo lápis da capa,
 * pelo lápis do autor, ou depois de um clique em qualquer vão do formulário. A
 * captura enxerga a tecla onde quer que o foco esteja.
 *
 * Nada a fazer com o PUT em voo: desistir deixaria a tela dizendo uma coisa e o
 * servidor gravando outra.
 */
function handleMetaEditKeydown(event) {
  if (event.key !== "Escape") return;
  if (!isEditingMeta.value || isSavingMeta.value) return;

  // Seletor aberto: o Esc é dele, para fechar a lista sem derrubar o
  // formulário inteiro por trás dela.
  if (event.target?.closest?.(".multiselect--active")) return;

  closeMetaEditKeepingDraft();
}

onMounted(() => window.addEventListener("keydown", handleMetaEditKeydown, true));
onUnmounted(() =>
  window.removeEventListener("keydown", handleMetaEditKeydown, true)
);

// Sair sem desistir - o Esc. O painel volta a ser painel, mas o que estava
// escrito espera ali e volta inteiro no próximo lápis, inclusive a capa escolhida
// e ainda não enviada. Some quando o leitor troca de texto (ou quando a tela é
// deixada, que desmonta tudo): fora daquele texto o rascunho não tem a que se
// aplicar.
function closeMetaEditKeepingDraft() {
  if (!isEditingMeta.value) return;
  pendingMetaDraft.value = {
    textId: textStore.selectedText?.textId ?? null,
    draft: metaDraft.value,
  };
  closeMetaEdit();
}

function closeMetaEdit() {
  isEditingMeta.value = false;
  metaDraft.value = null;
  metaError.value = "";
}

// O painel sai por "Cancelar", por "Salvar" ou pelo Esc - todas as três por
// gesto de quem edita. Havia aqui um ouvinte de clique fora, e ele fechava o
// formulário no meio de uma escolha: um clique no texto ao lado, na capa, em
// qualquer vão da tela, e o que estava sendo escrito saía da frente. Por isso
// saiu; as outras saídas continuam.
//
// Trocar de texto fecha a edição aberta - e isto não é o clique fora voltando
// pela janela: é o objeto editado saindo de baixo do formulário. O painel foi
// preenchido sobre UM texto, e "Salvar" grava no texto selecionado AGORA (ver
// saveMetaEdit), então deixá-lo aberto escreveria os campos de um no outro.
//
// E apaga o rascunho pendente, pelo mesmo motivo: ele foi escrito sobre OUTRO
// texto, e restaurá-lo aqui escreveria os campos de um no outro.
watch(
  () => textStore.selectedText?.textId,
  (textId) => {
    if (isEditingMeta.value) closeMetaEdit();

    if (pendingMetaDraft.value && pendingMetaDraft.value.textId !== textId) {
      pendingMetaDraft.value = null;
      resetCoverEdit();
    }
  }
);

async function saveMetaEdit() {
  if (isSavingMeta.value || !metaDraft.value) return;

  const draft = metaDraft.value;
  const title = (draft.title ?? "").trim();

  // Mesma exigência do cadastro: título é o único campo obrigatório do texto.
  if (!title) {
    metaError.value = "O título é obrigatório.";
    await nextTick();
    focusMetaFieldEnd(metaRefs.title);
    return;
  }

  isSavingMeta.value = true;
  metaError.value = "";

  try {
    await textStore.updateTextFields(textStore.selectedText.textId, {
      title,
      subtitle: (draft.subtitle ?? "").trim(),
      source: (draft.source ?? "").trim(),
      // 0 é como o backend lê "sem autor"/"sem gênero" — o EditTextForm manda o
      // mesmo valor pelo mesmo motivo.
      authorId: draft.author?.value ?? 0,
      categoryId: draft.category?.value ?? 0,
      topicIds: (draft.topics ?? [])
        .map((topic) => topic.value)
        .filter((id) => id != null),
      // Os dois só dizem algo quando o usuário mexeu na capa: nulo e falso são
      // lidos pelo textService como "não mandei nada sobre a capa", que é o que
      // preserva a que está gravada.
      coverBlob: coverBlob.value,
      removeCover: removeCover.value,
    });

    // Salvou: o rascunho já virou texto, e guardá-lo devolveria valores
    // velhos na próxima abertura.
    cancelMetaEdit();
    showSuccessToast("Texto atualizado com sucesso!");
  } catch (error) {
    // O painel permanece aberto com o que foi digitado: fechar aqui apagaria o
    // trabalho justamente quando ele não chegou ao servidor.
    metaError.value =
      error?.response?.data?.message ||
      error?.message ||
      "Não foi possível salvar o texto.";
  } finally {
    isSavingMeta.value = false;
  }
}

// ── Criação de autor, gênero e assunto de dentro do painel ───────────────────
// Mesmo caminho das tags no NoteList: o seletor não cria sozinho, abre o
// formulário de cadastro que o resto do sistema usa, já com o nome digitado.
// Criar calado com valores padrão seria um segundo cadastro, mais pobre e
// invisível. O registro volta para a lista de opções E já aplicado ao texto.
const showAddAuthorModal = ref(false);
const showAddCategoryModal = ref(false);
const showAddTopicModal = ref(false);
const newEntityName = ref("");

function startAuthorCreation(rawName) {
  newEntityName.value = toTitleCase(String(rawName ?? "").trim());
  showAddAuthorModal.value = true;
}
function startCategoryCreation(rawName) {
  newEntityName.value = toTitleCase(String(rawName ?? "").trim());
  showAddCategoryModal.value = true;
}
function startTopicCreation(rawName) {
  newEntityName.value = toTitleCase(String(rawName ?? "").trim());
  showAddTopicModal.value = true;
}

// Os três formulários devolvem { label, value }.
function handleAuthorAdded(option) {
  if (!authorOptions.value.some((a) => a.value === option.value)) {
    authorOptions.value.push(option);
  }
  if (metaDraft.value) metaDraft.value.author = option;
  showAddAuthorModal.value = false;
}

function handleCategoryAdded(option) {
  if (!categoryOptions.value.some((c) => c.value === option.value)) {
    categoryOptions.value.push(option);
  }
  if (metaDraft.value) metaDraft.value.category = option;
  showAddCategoryModal.value = false;
}

function handleTopicAdded(option) {
  if (!topicOptions.value.some((t) => t.value === option.value)) {
    topicOptions.value.push(option);
  }
  if (
    metaDraft.value &&
    !metaDraft.value.topics.some((t) => t.value === option.value)
  ) {
    metaDraft.value.topics.push(option);
  }
  showAddTopicModal.value = false;
}

// ── Capa, dentro do formulário ───────────────────────────────────────────────
// Três estados convivem, e é a distinção entre eles que impede um salvamento
// comum de apagar a capa gravada — a mesma regra do EditTextForm:
//
//   coverBlob != null  → imagem nova escolhida agora
//   removeCover        → o usuário pediu para tirar a capa
//   nenhum dos dois    → não mexeu; o backend mantém o que está gravado
const coverBlob = ref(null);
const coverPreview = ref("");
const coverError = ref("");
const removeCover = ref(false);
const isProcessingCover = ref(false);

// O que a miniatura do formulário mostra: a nova, se houver; senão a gravada, a
// menos que o usuário tenha pedido para remover.
const coverThumbUrl = computed(() => {
  if (coverPreview.value) return coverPreview.value;
  if (removeCover.value) return "";
  return coverUrl.value;
});

function releaseCoverPreview() {
  // Object URL não é coletado enquanto não for revogado.
  if (coverPreview.value) URL.revokeObjectURL(coverPreview.value);
  coverPreview.value = "";
}

function resetCoverEdit() {
  releaseCoverPreview();
  coverBlob.value = null;
  coverError.value = "";
  removeCover.value = false;
}

async function onCoverChange(event) {
  const file = event.target.files?.[0];
  // Zera o input: sem isso, escolher o MESMO arquivo depois de remover não
  // dispara change de novo.
  event.target.value = "";
  if (!file) return;

  coverError.value = "";
  isProcessingCover.value = true;
  try {
    // O recorte 3:4 e o redimensionamento acontecem no navegador, em
    // utils/coverImage.js — o mesmo caminho do cadastro do texto.
    const cover = await buildCoverFromFile(file);
    releaseCoverPreview();
    coverBlob.value = cover.blob;
    coverPreview.value = cover.previewUrl;
    // Escolher imagem nova cancela um pedido de remoção anterior.
    removeCover.value = false;
  } catch (e) {
    coverError.value = e.message || "Não foi possível processar a imagem.";
  } finally {
    isProcessingCover.value = false;
  }
}

function clearCoverEdit() {
  releaseCoverPreview();
  coverBlob.value = null;
  coverError.value = "";
  // Só vira pedido de remoção se havia capa gravada; descartar uma imagem que
  // ainda não foi salva não precisa dizer nada ao backend.
  removeCover.value = !!textStore.selectedText?.coverPath;
}

// `onUnmounted`, e não `onBeforeUnmount`: é o gancho que este componente já
// importa, e revogar um object URL depois da desmontagem dá no mesmo.
onUnmounted(releaseCoverPreview);

const isEditingContent = ref(false);
const isSavingContent = ref(false);
const editableContent = ref("");
const contentEditError = ref("");
const readerScrollEl = ref(null);
/**
 * Troca entre leitura e edição preservando a posição de rolagem.
 *
 * Sem isto o leitor perde o lugar: ao alternar, o renderizador é desmontado e o
 * editor montado no lugar, a altura do conteúdo muda e o navegador leva a rolagem
 * para o topo. Quem estava lendo a página 200 de Dom Casmurro voltaria ao começo
 * só por clicar em editar.
 *
 * A restauração acontece depois do `nextTick`, quando o novo componente já está no
 * DOM e o contêiner já tem a altura final.
 */
/**
 * Âncora de leitura: o PRIMEIRO trecho visível na tela quando se entrou em
 * edição — o que contém a primeira linha que estava sendo lida.
 *
 * Guardar o `scrollTop` não serve: ao sair da edição o conteúdo é remontado, a
 * altura muda, e a mesma coordenada em pixels cai noutro ponto do texto — ou é
 * limitada pelo navegador enquanto o layout ainda não terminou.
 *
 * A âncora guarda três coisas: o texto do trecho (que sobrevive ao deslocamento
 * de offsets que uma edição provoca), o `data-offset` dele (reserva, quando o
 * texto mudou) e a altura em que ele estava dentro da janela.
 *
 * Os dois lados usam a âncora de forma diferente, e é deliberado:
 *
 *   ENTRADA  o trecho vai para o TOPO do editor — editar começa onde a leitura
 *            estava, sem vão acima com texto já lido
 *   SAÍDA    o trecho volta à ALTURA que tinha na tela, para a leitura retomar
 *            no ponto exato em vez de recomeçar
 */
const contentAnchor = ref(null);
const richTextEditorEl = ref(null);

/** Primeiro nó de texto dentro do elemento, em ordem de documento. */
function firstTextNode(element) {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  return walker.nextNode();
}

/**
 * Índice do primeiro caractere VISÍVEL dentro de um nó de texto.
 *
 * A precisão pedida é de LINHA, não de parágrafo, e a linha não existe no DOM:
 * ela é produto da quebra que o navegador faz na hora de desenhar. O único jeito
 * de perguntar por ela é medir caracteres — um `Range` de um caractere devolve o
 * retângulo da linha em que ele caiu.
 *
 * A busca é binária porque a propriedade é monótona: caractere mais adiante no
 * texto nunca desenha acima de um anterior no mesmo bloco. Num parágrafo de 1500
 * caracteres são ~11 medições em vez de 1500.
 */
function firstVisibleCharIndex(textNode, boxTop) {
  const range = document.createRange();
  let lo = 0;
  let hi = Math.max(textNode.length - 1, 0);
  let best = 0;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    range.setStart(textNode, mid);
    range.setEnd(textNode, Math.min(mid + 1, textNode.length));
    const rect = range.getBoundingClientRect();

    // Retângulo vazio (espaço na dobra da linha) não decide nada: seguir para a
    // frente erra no máximo um caractere, enquanto tratá-lo como visível
    // ancoraria numa linha acima da que está na tela.
    const decided = rect.height > 0 || rect.width > 0;

    if (decided && rect.bottom > boxTop) {
      best = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }

  return best;
}

/**
 * Texto a partir de um ponto, atravessando os nós seguintes.
 *
 * A âncora precisa de material suficiente para ser reencontrada. Parar no fim do
 * nó deixaria uma agulha curta demais quando a linha visível cai no finzinho de
 * um trecho — e agulha curta casa no lugar errado.
 */
function collectTextFrom(root, startNode, startIndex, max) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let collected = "";
  let started = false;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!started) {
      if (node !== startNode) continue;
      started = true;
      collected += node.data.slice(startIndex);
    } else {
      collected += node.data;
    }
    if (collected.length >= max) break;
  }

  return collected.slice(0, max);
}

function captureContentAnchor() {
  const scroller = readerScrollEl.value;
  if (!scroller) return null;

  const spans = scroller.querySelectorAll("[data-offset]");
  if (!spans.length) return null;

  const box = scroller.getBoundingClientRect();
  let chosen = null;

  // O PRIMEIRO trecho visível — aquele que contém a primeira linha na tela.
  //
  // O teste é `bottom > box.top`, não `top >= box.top`: um parágrafo longo
  // começa acima da dobra e continua visível, e é ele que carrega a primeira
  // linha. Exigir que o trecho comece dentro da área pularia esse parágrafo e
  // ancoraria no seguinte — ou seja, adiante de onde a leitura está.
  for (const span of spans) {
    const rect = span.getBoundingClientRect();
    if (rect.bottom > box.top) {
      chosen = span;
      break;
    }
  }
  if (!chosen) return null;

  const spanStart = Number.parseInt(chosen.getAttribute("data-offset"), 10);
  const textNode = firstTextNode(chosen);
  if (!textNode) return null;

  // Dentro do trecho escolhido, o caractere onde a primeira LINHA visível
  // começa. É o que dá a precisão de linha: o trecho pode ter começado muito
  // acima da dobra, e ancorar no início dele traria de volta texto já lido.
  const index = firstVisibleCharIndex(textNode, box.top);

  const range = document.createRange();
  range.setStart(textNode, index);
  range.setEnd(textNode, Math.min(index + 1, textNode.length));

  return {
    offset: (Number.isFinite(spanStart) ? spanStart : 0) + index,
    // A raiz é o scroller inteiro, não o trecho: se a linha visível cai no fim de
    // um parágrafo, a agulha precisa continuar no parágrafo seguinte.
    text: collectTextFrom(scroller, textNode, index, 200),
    viewportTop: range.getBoundingClientRect().top - box.top,
  };
}

function placeContentAnchor(anchor) {
  const scroller = readerScrollEl.value;
  if (!scroller || !anchor) return;

  const spans = Array.from(scroller.querySelectorAll("[data-offset]"));
  if (!spans.length) return;

  const box = scroller.getBoundingClientRect();

  // 1) Pelo TEXTO, e com `indexOf`, não `startsWith`: a âncora agora começa na
  //    primeira LINHA visível, que quase nunca coincide com o início de um
  //    trecho. Procurar por prefixo não casaria mais nada.
  const needle = (anchor.text || "").slice(0, 40);
  if (needle) {
    for (const span of spans) {
      const at = (span.textContent || "").indexOf(needle);
      if (at < 0) continue;

      const textNode = firstTextNode(span);
      if (!textNode || at >= textNode.length) continue;

      const range = document.createRange();
      range.setStart(textNode, at);
      range.setEnd(textNode, Math.min(at + 1, textNode.length));

      const rect = range.getBoundingClientRect();
      if (!rect.height && !rect.width) continue;

      scroller.scrollTop += rect.top - box.top - anchor.viewportTop;
      return;
    }
  }

  // 2) Sem casar o texto (o trecho pode ter sido justamente o editado), cai no
  //    trecho de offset mais próximo — precisão de parágrafo, que é o que sobra.
  const target = spans.reduce((best, span) => {
    const value = Number.parseInt(span.getAttribute("data-offset"), 10);
    if (!Number.isFinite(value)) return best;
    if (!best) return span;
    const bestValue = Number.parseInt(best.getAttribute("data-offset"), 10);
    return Math.abs(value - anchor.offset) < Math.abs(bestValue - anchor.offset)
      ? span
      : best;
  }, null);
  if (!target) return;

  scroller.scrollTop +=
    target.getBoundingClientRect().top - box.top - anchor.viewportTop;
}

async function restoreContentAnchor() {
  const anchor = contentAnchor.value;
  if (!anchor) return;

  await nextTick();
  placeContentAnchor(anchor);

  // De novo no quadro seguinte: `nextTick` garante que o Vue atualizou o DOM, não
  // que o navegador terminou o layout de um texto de 371 mil caracteres. Antes
  // disso as medidas ainda mudam.
  requestAnimationFrame(() => placeContentAnchor(anchor));
}

// ── Seguir a leitura: a lista à direita mostra as notas do trecho na tela ────
//
// A varredura é por `IntersectionObserver`, não por evento de rolagem. A
// diferença não é estilo: o caminho por rolagem exigiria medir os spans a cada
// evento, e num texto de 371 mil caracteres são milhares de `getBoundingClientRect`
// por gesto — o navegador recalcula o layout em cada um. O observador faz a
// mesma conta fora do fio principal e só avisa quando algo entra ou sai.
//
// E ele observa SÓ os spans com `data-note-ids` — dezenas, não milhares.
//
// A contagem por nota existe porque um trecho vira VÁRIOS spans: ele é recortado
// nas fronteiras de outras notas e nos limites de bloco. Guardar um booleano
// faria o primeiro span a sair da tela apagar uma nota cujo resto ainda está
// visível.
// Folga SÓ ABAIXO da área visível: a nota entra na lista um pouco antes de
// chegar à tela.
//
// Simétrica, a folga valia nos dois sentidos, e a de cima puxava para a lista
// notas anteriores à primeira linha lida — que é o oposto de acompanhar a
// leitura. Acima da dobra a fronteira volta a ser a borda real.
//
// ⚠️ Nenhuma margem resolve o tremor de fronteira. Aumentá-la só desloca a
// fronteira; nota parada sobre a nova borda oscila igual, agora fora da vista.
// Quem resolve o tremor é a cadência abaixo: oscilações dentro da mesma janela se
// resolvem numa leitura só.
const NOTE_VISIBILITY_LEAD_MARGIN = "3%";

// Os dois tempos da cadência são SEPARADOS de propósito.
//
// Com um valor só, tornar a rolagem mais calma tornava o pouso mais lento junto,
// e o efeito se anulava: a lista ficava igualmente indiferente parada ou em
// movimento — nem lenta, nem responsiva, apenas imperceptível.
//
// Separados, cada um faz um trabalho:
//
//   CRUZEIRO  o quanto a lista se contém ENQUANTO se rola. É este que produz a
//             sensação de frenagem — quanto maior, mais ela "pesa" no gesto.
//   POUSO     o quanto ela espera para assentar DEPOIS que a rolagem para. Curto
//             de propósito: parar é o sinal de que se quer ver o resultado.
//
// Valores de CALIBRAGEM, não constantes deduzidas: dependem de quão rápido se
// rola e de quão densas são as notas no texto.
// 800, e não 500: o incômodo relatado foi a lista PISCAR, e piscar é número de
// trocas, não velocidade de cada troca. Uma ficha que entra e sai dentro de meio
// segundo aparece como lampejo; espaçar as leituras reduz esses lampejos na
// origem, sem animar nada.
const NOTE_VISIBILITY_CRUISE_MS = 800;
const NOTE_VISIBILITY_SETTLE_MS = 250;

const visibleNoteSpanCounts = new Map();
let noteVisibilityObserver = null;
let visibilitySettleTimer = 0;
let lastVisibilityFlushAt = Number.NEGATIVE_INFINITY;

function noteIdsFromSpan(span) {
  const raw = span.getAttribute("data-note-ids");
  if (!raw) return [];
  return raw
    .split(",")
    .map((value) => Number.parseInt(value, 10))
    .filter(Number.isFinite);
}

/**
 * Cadência da lista: responde na hora, depois desliza e assenta.
 *
 * Antes era um quadro de `requestAnimationFrame`, o que permitia até 60
 * atualizações por segundo durante um gesto — a lista virava um piscar.
 *
 * São duas regras trabalhando juntas, e cada uma resolve um problema:
 *
 *   BORDA DE ENTRADA  a primeira mudança depois de um período parado aplica
 *                     imediatamente. Sem ela a lista pareceria morta durante o
 *                     gesto, e numa rolagem longa e contínua não atualizaria
 *                     nunca — o pior dos mundos, porque parece quebrado.
 *
 *   POUSO             enquanto as mudanças continuam chegando, o relógio é
 *                     reiniciado. A lista só assenta quando a rolagem descansa.
 *
 * O efeito combinado é uma atualização a cada `NOTE_VISIBILITY_CRUISE_MS`
 * durante a rolagem, mais uma última `NOTE_VISIBILITY_SETTLE_MS` depois de parar.
 */
function scheduleVisibleNotesFlush() {
  if (performance.now() - lastVisibilityFlushAt >= NOTE_VISIBILITY_CRUISE_MS) {
    flushVisibleNotesNow();
    return;
  }

  if (visibilitySettleTimer) clearTimeout(visibilitySettleTimer);
  visibilitySettleTimer = setTimeout(
    flushVisibleNotesNow,
    NOTE_VISIBILITY_SETTLE_MS
  );
}

function flushVisibleNotesNow() {
  if (visibilitySettleTimer) {
    clearTimeout(visibilitySettleTimer);
    visibilitySettleTimer = 0;
  }
  lastVisibilityFlushAt = performance.now();
  flushVisibleNotes();
}

function flushVisibleNotes() {
  if (!noteStore.followReading) return;

  // Atividade sem nota nenhuma — ou notas ainda não carregadas — não produz
  // recorte. Sem esta guarda, o observador rodaria antes da carga e a coluna
  // diria "nenhuma nota no trecho em leitura" quando o certo é o estado vazio da
  // atividade, com as ações de criar nota.
  if (!noteStore.allNotes || noteStore.allNotes.length === 0) return;

  // Na ORDEM DO TEXTO, não na ordem em que o observador avisou: a lista tem de
  // ler como o texto lê. É o mesmo comparador que numera os expoentes.
  const byId = new Map(
    noteStore.allNotes.map((note) => [note.noteId, note])
  );

  const visible = [...visibleNoteSpanCounts.keys()]
    .map((id) => byId.get(id))
    .filter(Boolean)
    .sort(compareNotesByTextOrder)
    .map((note) => note.noteId);

  noteStore.setReadingVisibleNotes(visible);
}

function handleNoteIntersections(entries) {
  for (const entry of entries) {
    for (const id of noteIdsFromSpan(entry.target)) {
      const count = visibleNoteSpanCounts.get(id) ?? 0;
      if (entry.isIntersecting) {
        visibleNoteSpanCounts.set(id, count + 1);
      } else if (count <= 1) {
        visibleNoteSpanCounts.delete(id);
      } else {
        visibleNoteSpanCounts.set(id, count - 1);
      }
    }
  }
  scheduleVisibleNotesFlush();
}

function stopNoteVisibilityObserver() {
  if (noteVisibilityObserver) {
    noteVisibilityObserver.disconnect();
    noteVisibilityObserver = null;
  }
  if (visibilitySettleTimer) {
    clearTimeout(visibilitySettleTimer);
    visibilitySettleTimer = 0;
  }
  visibleNoteSpanCounts.clear();
}

/**
 * Refaz a observação do zero.
 *
 * Precisa ser do zero, e não incremental: um span que é removido do DOM enquanto
 * está visível nunca dispara a saída, e a contagem dele ficaria pendurada para
 * sempre. Como o renderizador remonta a árvore inteira quando as notas ou o
 * conteúdo mudam, reconectar é mais barato e mais seguro do que reconciliar.
 */
async function refreshNoteVisibilityObserver() {
  stopNoteVisibilityObserver();

  if (!noteStore.followReading || isEditingContent.value) return;

  await nextTick();
  const scroller = readerScrollEl.value;
  if (!scroller) return;

  noteVisibilityObserver = new IntersectionObserver(handleNoteIntersections, {
    root: scroller,
    // topo direita baixo esquerda — zero em cima, folga só embaixo.
    rootMargin: `0px 0px ${NOTE_VISIBILITY_LEAD_MARGIN} 0px`,
    threshold: 0,
  });

  const spans = scroller.querySelectorAll("[data-note-ids]");
  for (const span of spans) noteVisibilityObserver.observe(span);

  // Com spans a observar, a primeira chamada do observador já aplica o recorte.
  // Sem nenhum, ela nunca vem — e o texto sem nota precisa produzir lista vazia,
  // que é a resposta combinada para trecho sem nota.
  // Direto, sem passar pela cadência: reconectar o observador não é gesto de
  // rolagem, e adiar aqui deixaria a lista defasada depois de um filtro.
  if (spans.length === 0) flushVisibleNotesNow();
}

/**
 * Leva a rolagem INTERNA do editor até o trecho onde a leitura estava.
 *
 * O editor abre no topo da tela, com o painel de metadados oculto — mas abrir no
 * topo é sobre a CAIXA, não sobre o conteúdo dela: quem estava no capítulo 40 de
 * Dom Casmurro deve continuar no capítulo 40.
 *
 * A busca é por TEXTO, não por offset, e a diferença importa. O DOM do editor não
 * é o mesmo do renderizador: o ProseMirror insere `<br class="ProseMirror-trailingBreak">`
 * em parágrafo vazio, e cada um desses emite uma quebra na projeção que o conteúdo
 * gravado não tem. Um offset gravado apontaria para o lugar errado; o texto do
 * trecho continua sendo ele mesmo. O offset fica como reserva, para quando o
 * trecho tiver sido justamente o editado.
 *
 * `toPlainText` devolve o mapa com o TEXT NODE de cada segmento, e é ele que
 * transforma uma posição na projeção em coordenada de tela, via `Range`.
 */
function placeAnchorInsideEditor() {
  const anchor = contentAnchor.value;
  if (!anchor) return;

  const scroller = readerScrollEl.value?.querySelector(".rte-content");
  const root = scroller?.querySelector(".ProseMirror");
  if (!scroller || !root) return;

  const { text, map } = toPlainText(root);
  if (!text) return;

  const needle = (anchor.text || "").slice(0, 40);
  let target = needle ? text.indexOf(needle) : -1;
  if (target < 0) {
    target = Math.min(anchor.offset ?? 0, Math.max(text.length - 1, 0));
  }

  const segment = map.find(
    (s) => s.node && target >= s.start && target < s.end
  );
  if (!segment) return;

  const within = Math.min(
    Math.max(target - segment.start, 0),
    Math.max(segment.node.length - 1, 0)
  );

  const range = document.createRange();
  range.setStart(segment.node, within);
  range.setEnd(segment.node, Math.min(within + 1, segment.node.length));

  const rect = range.getBoundingClientRect();
  const box = scroller.getBoundingClientRect();
  if (!rect.height && !rect.width) return;

  // No TOPO do editor, não na altura que o trecho tinha na tela: o pedido é que
  // a edição comece na primeira linha que estava sendo lida. Preservar a altura
  // original deixaria um vão acima dela, com texto que o leitor já passou.
  //
  // `viewportTop` continua guardado na âncora e continua sendo usado na SAÍDA,
  // onde a exigência é outra: lá o objetivo é devolver a leitura ao ponto exato
  // em que ela estava, e não recomeçá-la.
  scroller.scrollTop += rect.top - box.top;
}

// A instância do Tiptap só existe depois do `useEditor` assíncrono, e é na mesma
// virada que o `watch` interno do RichTextEditor aplica o conteúdo. `nextTick`
// garante o DOM atualizado; o quadro extra garante o layout de um texto longo.
watch(
  () => richTextEditorEl.value?.editor,
  async (instance) => {
    if (!instance || !isEditingContent.value) return;
    await nextTick();
    requestAnimationFrame(() => placeAnchorInsideEditor());
  }
);

// ── Precedência do mouse: a lista começa pela nota apontada ─────────────────
//
// O ponteiro sobre um destaque não muda QUAIS notas a lista mostra — o conjunto
// continua sendo o do trecho na tela. Ele muda por qual delas a coluna começa.
//
// `mouseover` dispara a cada pixel dentro do mesmo span; comparar com o último
// id evita reagir a movimento que não mudou de nota.
let lastHoveredNoteId = null;

function onReadingAreaMouseover(event) {
  const span = event.target?.closest?.("[data-note-ids]");
  if (!span) {
    lastHoveredNoteId = null;
    return;
  }

  const ids = span
    .getAttribute("data-note-ids")
    .split(",")
    .map((value) => Number.parseInt(value, 10))
    .filter(Number.isFinite);
  if (ids.length === 0) return;

  // Destaques sobrepostos trazem várias notas no mesmo span. Escolhe-se a
  // PRIMEIRA na ordem do texto: as sobrepostas são vizinhas na lista, então
  // começar pela mais antiga põe o grupo inteiro no topo, e não o meio dele.
  const byId = new Map(
    (noteStore.allNotes || []).map((note) => [note.noteId, note])
  );
  const first = ids
    .map((id) => byId.get(id))
    .filter(Boolean)
    .sort(compareNotesByTextOrder)[0];
  if (!first || first.noteId === lastHoveredNoteId) return;

  lastHoveredNoteId = first.noteId;
  noteStore.hoveredNoteId = first.noteId;
}

/**
 * Copia o conteúdo inteiro do texto, com a formatação.
 *
 * A fonte é o `fileContent` CRU, e não o DOM do renderizador. O que o renderizador
 * acrescenta é do LEITOR, não do documento: cada nó de texto vira um
 * `<span data-offset>`, os trechos anotados ganham grifo em `style` INLINE — que
 * viaja com a colagem —, e os expoentes e os fólios de margem são caracteres de
 * verdade na árvore. Tudo isso desembarcaria no Word ou no Docs junto com o texto.
 *
 * Dois formatos no MESMO item da área de transferência: quem cola num editor de
 * texto rico recebe o `text/html` e mantém negrito, títulos, listas e tabelas;
 * quem cola no bloco de notas recebe o `text/plain`. O texto puro vem da projeção
 * — a mesma régua onde os offsets das notas são medidos —, que emite `
` nas
 * fronteiras de bloco em vez de colar o fim de um parágrafo no início do outro.
 *
 * ⚠️ O `ClipboardItem` é montado ANTES do primeiro `await`. O Safari só aceita a
 * escrita enquanto o gesto do usuário ainda vale, e esperar antes de montá-lo
 * fecharia essa janela.
 */
async function copyContent() {
  const content = textStore.fileContent || "";
  if (!content) {
    showErrorToast("Não há conteúdo para copiar.");
    return;
  }

  const plain = isPlaintextContent.value ? content : toPlainText(content).text;

  try {
    // Em texto puro não há formatação a preservar; sem `ClipboardItem` (Firefox
    // antigo) não há como escrever dois formatos. Nos dois casos, copiar só o
    // texto é melhor do que não copiar.
    if (isPlaintextContent.value || typeof ClipboardItem === "undefined") {
      await navigator.clipboard.writeText(plain);
      showSuccessToast("Texto copiado.");
      return;
    }

    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([content], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
      }),
    ]);
    showSuccessToast("Texto copiado com a formatação.");
  } catch (error) {
    console.error("Erro ao copiar o conteúdo do texto:", error);

    try {
      await navigator.clipboard.writeText(plain);
      showSuccessToast("Texto copiado, mas sem a formatação.");
    } catch {
      // `navigator.clipboard` exige contexto seguro: fora de HTTPS (ou de
      // localhost) ele sequer existe, e não há segunda tentativa possível.
      showErrorToast("Não foi possível copiar o texto.");
    }
  }
}

// ── Menu ⋯ da área de leitura ────────────────────────────────────────────────
// Reúne copiar, editar e exportar num só botão, no canto da folha. Editar segue
// a regra de sempre (canEditContent); copiar e exportar são de todos (exportar
// é o que o botão de PDF da lista de notas fazia antes de sair de lá).
const isContentMenuOpen = ref(false);
const contentMenuEl = ref(null);
const showPrintOptions = ref(false);

function handleContentMenuClickOutside(event) {
  if (!isContentMenuOpen.value) return;
  if (contentMenuEl.value && contentMenuEl.value.contains(event.target)) return;
  isContentMenuOpen.value = false;
}

onMounted(() =>
  document.addEventListener("click", handleContentMenuClickOutside, true)
);
onUnmounted(() =>
  document.removeEventListener("click", handleContentMenuClickOutside, true)
);

function copyContentFromMenu() {
  isContentMenuOpen.value = false;
  copyContent();
}

function startContentEditFromMenu() {
  isContentMenuOpen.value = false;
  startContentEdit();
}

function openPrintOptions() {
  isContentMenuOpen.value = false;
  showPrintOptions.value = true;
}

function startContentEdit() {
  if (!canEditContent.value) return;

  // Marca onde a leitura estava ANTES de trocar o renderizador pelo editor — é a
  // última chance de ler os [data-offset], que só existem no renderizador.
  contentAnchor.value = captureContentAnchor();

  editableContent.value = textStore.fileContent || "";
  contentEditError.value = "";
  isEditingContent.value = true;
}

async function cancelContentEdit() {
  isEditingContent.value = false;
  editableContent.value = "";
  contentEditError.value = "";
  await restoreContentAnchor();
}

/**
 * Esc fecha o editor de conteúdo — o mesmo que o ✕ da faixa.
 *
 * Ouvinte na JANELA e em fase de CAPTURA, de propósito. Um `@keydown.esc` no
 * editor só responde com o cursor dentro da caixa de texto: basta clicar na
 * moldura, na barra de ferramentas ou em qualquer vão do painel para o foco ir
 * embora e a tecla emudecer. A captura na janela é o único ponto que enxerga a
 * tecla independentemente de onde o foco esteja e antes de qualquer handler do
 * caminho — nenhum `stopPropagation` de componente intermediário a esconde.
 *
 * O preço da captura é ficar na frente de quem também usa Esc, então a tela
 * cheia do editor é tratada aqui, à mão: expandido, o primeiro Esc apenas sai
 * da tela cheia. Sem isso, uma tecla só faria as duas coisas — e, com o foco
 * fora da caixa, o handler interno do editor nem chegaria a rodar.
 *
 * O guarda de `isSavingContent` repete o `:disabled` do botão Descartar: com o
 * PUT em voo, desistir deixaria a tela dizendo uma coisa e o servidor gravando
 * outra.
 */
function handleContentEditKeydown(event) {
  if (event.key !== "Escape") return;
  if (!isEditingContent.value || isSavingContent.value) return;

  if (richTextEditorEl.value?.isExpanded) {
    richTextEditorEl.value.toggleExpand();
    return;
  }

  cancelContentEdit();
}

onMounted(() =>
  window.addEventListener("keydown", handleContentEditKeydown, true)
);
onUnmounted(() =>
  window.removeEventListener("keydown", handleContentEditKeydown, true)
);

/**
 * Salva o conteúdo editado.
 *
 * ⚠️ O payload precisa ir COMPLETO. O endpoint de edição trata `Permissions`
 * ausente como "apagar todas" — mandar só o conteúdo destruiria as permissões do
 * texto em silêncio. Por isso as permissões atuais são lidas e devolvidas
 * inalteradas, junto com os demais metadados.
 */
async function saveContentEdit() {
  const text = textStore.selectedText;
  if (!text || isSavingContent.value) return;

  isSavingContent.value = true;
  contentEditError.value = "";

  try {
    const current = await textStore.fetchTextPermissions(text.textId);
    const permissions = (current || []).map((p) => ({
      entityId: p.userId != null ? p.userId : p.groupId,
      permissionId: p.permissionId,
      type: p.userId != null ? "user" : "group",
    }));

    const isHtml = text.contentFormat === "html";

    const result = await textStore.updateText({
      textId: text.textId,
      title: text.title,
      subtitle: text.subtitle,
      // `synopsis` faltava aqui e o serviço manda `data.synopsis || ""`: o
      // controller atribui o campo sem condição, então salvar o CONTEÚDO
      // apagava a sinopse do texto de raspão. Todo campo que o PUT toca precisa
      // ir de volta, mesmo os que esta edição rápida não mexe.
      synopsis: text.synopsis ?? "",
      source: text.source,
      active: text.active,
      categoryId: text.categoryId,
      authorId: text.authorId,
      topicIds: text.topicIds ?? [],
      permissions,
      pastedContent: editableContent.value,
      pastedContentIsHtml: isHtml,
      file: null,
    });

    // O backend recusa quando a mudança apagaria trecho anotado, devolvendo as
    // notas impactadas para confirmação. Esse fluxo tem tela própria no
    // EditTextForm; aqui a edição rápida apenas se recusa a decidir por conta.
    if (result?.requiresConfirmation) {
      contentEditError.value =
        "Esta alteração afeta trechos anotados. Use a edição completa do texto para revisar as notas impactadas.";
      return;
    }

    isEditingContent.value = false;
    await handleTextConverted(); // recarrega metadado, conteúdo e notas

    // Volta ao trecho onde a leitura estava, pela âncora de TEXTO capturada ao
    // entrar em edição — o conteúdo acabou de ser remontado e as coordenadas
    // antigas não valem mais.
    await restoreContentAnchor();
  } catch (error) {
    contentEditError.value =
      error?.response?.data?.message ??
      error?.message ??
      "Não foi possível salvar o conteúdo.";
  } finally {
    isSavingContent.value = false;
  }
}

// Depois de converter, mudaram no servidor: o conteúdo do arquivo, os offsets das
// notas e o contentFormat. Recarregar os três é o que faz a tela trocar de
// renderizador sem o usuário precisar atualizar a página.
//
// A ordem importa: o metadado primeiro, porque é dele que sai o filepath usado
// para reler o conteúdo — e é ele que decide qual renderizador monta.
async function handleTextConverted() {
  const textId = textStore.idText;
  if (!textId) return;

  await textStore.getTextById(textId);

  const filepath = textStore.selectedText?.filepath;
  if (filepath) await textStore.fetchTextFileContent(filepath);

  if (activityStore.idActivity) {
    // PRIMEIRO `allNotes`, e com `force`.
    //
    // É ele que alimenta `notesForHighlight`, ou seja, o REALCE do texto — e a
    // lista paginada não o atualiza. Sem esta linha, o conteúdo aparecia
    // convertido enquanto as marcações continuavam nos offsets de antes: o texto
    // certo com os destaques deslocados. Sair da rota e voltar "consertava"
    // porque a atividade era recarregada e o cache se refazia.
    //
    // `force` é obrigatório: o atalho de cache do `getNotesByActivityId` devolve
    // a cópia guardada na atividade, que aqui é justamente a desatualizada.
    await noteStore.getNotesByActivityId(
      activityStore.idActivity,
      true,
      true,
      noteStore.filterSortOrder,
      [],
      { force: true }
    );

    await noteStore.fetchNotesByActivityIdPaged(
      activityStore.idActivity,
      noteStore.currentPage ?? 1,
      noteStore.pageSize,
      noteStore.searchTerm
    );
  }
}

provide("multiSelectedExcerpts", multiSelectedExcerpts);

const isNoteModalVisible = ref(false);
const selectedNoteId = ref(null);

function openNoteModal(noteId) {
  selectedNoteId.value = noteId;
  isNoteModalVisible.value = true;
}

async function onNoteClicked(noteId) {
  if (!noteId) return;

  // ── Seguindo a leitura, não há página a buscar ──────────────────────────
  //
  // A lista É o recorte do trecho na tela, e a nota clicada está nele por
  // construção: ela foi clicada na tela. O caminho de baixo — descobrir em que
  // página ela cairia e carregar essa página — contradiz o modo em dois pontos:
  //
  //   1. `fetchNotesByActivityIdPaged` começa com `clearNoteFocus()`, então a
  //      lista deixa de acompanhar a leitura e vira uma página qualquer;
  //   2. a primeira rolagem seguinte reinstala o recorte e desfaz tudo.
  //
  // O `flush` antes de iluminar sincroniza a lista com o que está na tela agora:
  // sem ele, uma nota que entrou na tela dentro da janela de frenagem ainda não
  // teria ficha, e o realce não acharia onde pousar.
  if (noteStore.noteFocusMode === "reading") {
    flushVisibleNotesNow();
    noteStore.highlightNoteId = noteId;
    return;
  }

  const allNotes = noteStore.allNotes;
  const idx = allNotes.findIndex((n) => n.noteId === noteId);
  if (idx === -1) return;

  const pageSize = noteStore.pageSize;
  const targetPage = Math.ceil((idx + 1) / pageSize);

  await noteStore.fetchNotesByActivityIdPaged(
    activityStore.idActivity,
    targetPage,
    pageSize,
    noteStore.searchTerm
  );

  noteStore.highlightNoteId = noteId;
}

// Clique no trecho destacado: recorta a lista para as notas que cobrem aquele
// ponto. Diferente do expoente acima, não troca de página nem ilumina ficha —
// a lista inteira passa a ser o recorte, e o NoteList oferece o "ver todas".
function onNotesFocused(noteIds) {
  if (!Array.isArray(noteIds) || noteIds.length === 0) return;
  noteStore.focusNotesByIds(noteIds);
}

function onNoteDetails(noteId) {
  if (!noteId) return;
  openNoteModal(noteId);
}

const showTextCardModal = ref(false);
function openTextCard() {
  showTextCardModal.value = true;
}
function closeTextCard() {
  showTextCardModal.value = false;
}

// Após editar o texto (via TextCardModal → EditTextForm), o backend recalcula
// os ranges das notas impactadas (RangeShiftOnly) ou desvincula/apaga as
// críticas. Sem ressincronizar, o corpo do texto atualiza mas os destaques
// ficam nos offsets ANTIGOS — desalinhados sobre o texto novo.
//
// Ponto crítico da correção: os destaques (TextWithNotes) vêm de
// noteStore.notesForHighlight, que deriva de noteStore.allNotes — NÃO de
// noteStore.notes. fetchNotesByActivityIdPaged atualiza apenas `notes` (a lista
// paginada do painel), por isso sozinho não corrigia os ranges. A sequência
// abaixo espelha o padrão de updateNote/updateNotesBatch:
//   1) getActivityById → refaz do backend selectedActivity.allNotes (ranges já
//      recalculados) e também o fileContent (via getTextById interno);
//   2) getNotesByActivityId → copia esse cache fresco para noteStore.allNotes,
//      que é a fonte dos destaques;
//   3) fetchNotesByActivityIdPaged → atualiza a lista paginada do painel.
//
// try/catch: este mesmo evento também é emitido no fluxo de EXCLUSÃO de texto
// (TextCardModal.handleDeleteClose). O getActivityById trata internamente a
// falha do getTextById do texto removido; o catch cobre o restante, e o "close"
// que acompanha o delete fecha o modal normalmente.
// ── Menu ••• do painel do texto ──────────────────────────────────────────────
// Três destinos que não cabem num lápis: ver o card do texto, ver quem tem
// acesso a ele e abrir o formulário completo. Os lápis editam campo a campo no
// próprio painel; este menu é para o que exige a tela inteira.
const isTextMenuOpen = ref(false);
const showTextPermissionModal = ref(false);
const showEditTextModal = ref(false);

function toggleTextMenu() {
  isTextMenuOpen.value = !isTextMenuOpen.value;
}

// Clique fora fecha o menu. Na fase de captura, como o resto do painel, para o
// clique que abre outra coisa não deixar este aberto atrás.
function handleTextMenuClickOutside(event) {
  if (!isTextMenuOpen.value) return;
  if (event.target.closest?.(".text-menu-container")) return;
  isTextMenuOpen.value = false;
}

onMounted(() =>
  document.addEventListener("click", handleTextMenuClickOutside, true)
);
onUnmounted(() =>
  document.removeEventListener("click", handleTextMenuClickOutside, true)
);

function openTextDetailsFromMenu() {
  isTextMenuOpen.value = false;
  openTextCard();
}

function openTextPermissions() {
  isTextMenuOpen.value = false;
  showTextPermissionModal.value = true;
}

function openEditTextModal() {
  isTextMenuOpen.value = false;
  showEditTextModal.value = true;
}

function handleEditTextModalClose() {
  showEditTextModal.value = false;
}

// O formulário completo pode ter mexido em tudo — inclusive no conteúdo e nos
// vínculos das notas. Ressincroniza pelo mesmo caminho que o card do texto usa,
// senão a área de leitura seguiria mostrando a versão anterior.

async function handleTextUpdatedFromModal() {
  showEditTextModal.value = false;
  await handleTextUpdated();
}

async function handleTextUpdated() {
  const activityId = activityStore.idActivity;
  if (!activityId) return;
  try {
    await activityStore.getActivityById(activityId);

    await noteStore.getNotesByActivityId(
      activityId,
      noteStore.filterIncludeOwn,
      noteStore.filterIncludeComments,
      noteStore.filterSortOrder
    );

    await noteStore.fetchNotesByActivityIdPaged(
      activityId,
      noteStore.currentPage,
      noteStore.pageSize,
      noteStore.searchTerm
    );
  } catch (err) {
    console.warn(
      "[TextDisplayer] Falha ao ressincronizar texto/notas após edição:",
      err?.message || err
    );
  }
}

const showAuthorCardModal = ref(false);
const selectedAuthorId = ref(null);

// ── Arquivos servidos pela API (avatar, capa) ────────────────────────────────
// O banco guarda caminho relativo; o arquivo vem do wwwroot da API, que fica
// noutra origem que a do front. Daí montar a URL a partir do baseURL do axios,
// sem o sufixo /api. A capa e o avatar do criador fazem a MESMA conta — é uma
// função só, como no TextCardsForm.
function mediaUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

// Capa enviada pelo usuário ("uploads/covers/<guid>.webp"). Vazia quando o
// texto não tem imagem — aí entra a capa desenhada, que sempre existe.
const coverUrl = computed(() => mediaUrl(textStore.selectedText?.coverPath));

// ── Metadado "Inserido por": avatar + data de criação do texto ───────────────
const creatorAvatarUrl = computed(() =>
  mediaUrl(textStore.selectedText?.createdByUserPhotoPath)
);

function formatCreatedDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR");
}

// ── Tamanho do texto ─────────────────────────────────────────────────────────
// A equivalência mora numa constante porque aparece DUAS vezes: na conta das
// páginas e na dica que explica a conta. Escritas à mão, as duas divergiriam na
// primeira vez que o número mudasse, e a dica passaria a mentir sem errar nada
// visível.
const CHARS_PER_PAGE = 2450;

const pageEquivalenceHint = `Uma página equivale a aproximadamente ${CHARS_PER_PAGE.toLocaleString(
  "pt-BR"
)} caracteres de texto`;

// "12.345 caracteres · 5,04 páginas". Os dois números em pt-BR: `toFixed`
// devolvia "5.04", com ponto decimal, no meio de um texto em português.
const textLengthLabel = computed(() => {
  const chars = Number(textStore.selectedText?.textLength) || 0;
  const pages = (chars / CHARS_PER_PAGE).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${chars.toLocaleString("pt-BR")} caracteres · ${pages} páginas`;
});

// Fecho do rodapé: ", em {data}", colado no nome. Texto vazio quando não há
// data — um ", em" pendurado no fim da linha lê como falha, não como ausência.
const createdAtSuffix = computed(() => {
  const d = formatCreatedDate(textStore.selectedText?.createdAt);
  return d ? `, em ${d}` : "";
});

// ── Card do criador do texto (UserCardModal) ─────────────────────────────────
const showUserCardModal = ref(false);
const selectedUserId = ref(null);
function openUserCardModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserCardModal.value = true;
}
function handleUserCardModalClose() {
  showUserCardModal.value = false;
  selectedUserId.value = null;
}

function openAuthorCard(authorId) {
  if (!authorId) return;
  selectedAuthorId.value = authorId;
  showAuthorCardModal.value = true;
}
function closeAuthorCard() {
  showAuthorCardModal.value = false;
  selectedAuthorId.value = null;
}

const showActivityCardModal = ref(false);
const selectedActivityId = ref(null);

function openActivityCard(activityId) {
  const id = activityId ?? activityStore.idActivity;
  if (!id) return;
  selectedActivityId.value = id;
  showActivityCardModal.value = true;
}
function closeActivityCard() {
  showActivityCardModal.value = false;
  selectedActivityId.value = null;
}

// ✅ Card modals Categoria / Assunto
const showCategoryCardModal = ref(false);
const selectedCategoryId = ref(null);
function openCategoryCard(categoryId) {
  if (!categoryId && categoryId !== 0) return;
  selectedCategoryId.value = Number(categoryId);
  showCategoryCardModal.value = true;
}
function closeCategoryCard() {
  showCategoryCardModal.value = false;
  selectedCategoryId.value = null;
}

const showTopicCardModal = ref(false);
const selectedTopicId = ref(null);
function openTopicCard(topicId) {
  if (topicId == null) return;
  selectedTopicId.value = Number(topicId);
  showTopicCardModal.value = true;
}
function closeTopicCard() {
  showTopicCardModal.value = false;
  selectedTopicId.value = null;
}

// ── Chips de classificação: gênero + assuntos ────────────────────────────────
// A composição vem de utils/textCatalog.js — são as MESMAS chips que a estante
// (TextCardsForm.vue) mostra para o mesmo texto, inclusive as apagadas de campo
// vazio. Sem `limit`: o corte em "+N" é restrição de altura de card, e este
// painel não tem fileira a alinhar.
const metaChips = computed(() => catalogChips(textStore.selectedText).visible);

// Gênero e assuntos vêm juntos do catálogo (é assim que a estante os mostra),
// mas aqui cada um tem a sua fileira e o seu lápis. Quem diz de qual campo a
// chip fala é o `field`, e não o `kind`: a chip de ausência é "empty" nos dois.
const genreChips = computed(() =>
  metaChips.value.filter((chip) => chip.field === "genre")
);
const topicChipList = computed(() =>
  metaChips.value.filter((chip) => chip.field === "topic")
);

// Campo preenchido ganha lápis; campo vazio não, porque a própria chip de
// ausência já abre a edição — e dois alvos para a mesma coisa, lado a lado,
// seriam um a mais.
const hasGenreInfo = computed(() => !!textStore.selectedText?.categoryName);
const hasTopicsInfo = computed(() =>
  (textStore.selectedText?.topicNames ?? []).some(Boolean)
);
const hasSourceInfo = computed(
  () => String(textStore.selectedText?.source ?? "").trim() !== ""
);

// A fonte tem fileira própria, abaixo das outras chips: é procedência, e não
// classificação — enfileirada junto com gênero e assunto, leria como mais uma
// delas.
const metaSourceChip = computed(() => sourceChip(textStore.selectedText));

// Nem toda chip leva a algum lugar: fonte é texto livre e ausência não é nada —
// nenhuma das duas tem card a abrir. Prometer o clique e não cumprir é pior que
// não prometer, então só gênero e assunto viram botão.
function isLinkedChip(chip) {
  return (chip.kind === "genre" || chip.kind === "topic") && chip.id != null;
}

// A chip sabe o que nomeia e qual é o id; o painel só decide qual card abrir.
function openChipCard(chip) {
  if (chip.kind === "genre") openCategoryCard(chip.id);
  else if (chip.kind === "topic") openTopicCard(chip.id);
}

// Só nome próprio catalogado passa por toTitleCase. Fonte é texto livre — pode
// ser uma URL ou uma citação —, e rótulo de ausência é frase: recapitular
// qualquer um dos dois estragaria o que está escrito.
function chipText(chip) {
  return chip.kind === "genre" || chip.kind === "topic"
    ? toTitleCase(chip.name)
    : chip.name;
}

const CHIP_KIND_LABELS = { genre: "gênero", topic: "assunto", source: "fonte" };

function chipTitle(chip) {
  const label = CHIP_KIND_LABELS[chip.kind];
  if (!label) return chip.name;
  return isLinkedChip(chip)
    ? `Abrir ${label}: ${chip.name}`
    : `${label[0].toUpperCase()}${label.slice(1)}: ${chip.name}`;
}

const isModalHidden = ref(true);
provide("isModalHidden", isModalHidden);

const selectedText = ref("");
provide("selectedText", selectedText);

provide("isMultiSelectMode", isMultiSelectMode);

const offsetStart = ref(null);
const offsetEnd = ref(null);

// Criação rápida: a seleção vira nota na hora, só com o título, sem passar pelo
// formulário. É o padrão do leitor; o botão da barra devolve o modal a quem
// quiser preencher corpo, fonte, tags ou permissões antes de salvar.
const addNoteFormRef = ref(null);
const isQuickNoteMode = ref(true);

function toggleQuickNoteMode() {
  isQuickNoteMode.value = !isQuickNoteMode.value;
}

async function onSelectionMade({
  offsetStart: start,
  offsetEnd: end,
  selectedText: text,
}) {
  if (!text || start == null || end == null || start >= end) {
    if (!isMultiSelectMode.value) {
      isModalHidden.value = true;
    }
    return;
  }

  const trimmed = String(text).trim();
  if (!trimmed) {
    if (!isMultiSelectMode.value) {
      isModalHidden.value = true;
    }
    return;
  }

  // Vínculo não permite anotar nesta série: o duplo clique e o arrasto avisam e
  // param aqui. Sem isso, a seleção seguia abrindo o formulário (ou criando a
  // nota rápida) para morrer no 403 do backend.
  if (!memberCanCreateNote.value) {
    isModalHidden.value = true;
    showErrorToast(blockedByMembershipHint);
    return;
  }

  if (isMultiSelectMode.value) {
    const last = multiSelectedExcerpts.value.length
      ? multiSelectedExcerpts.value[multiSelectedExcerpts.value.length - 1]
      : null;

    if (
      last &&
      last.start === start &&
      last.end === end &&
      last.text === trimmed
    ) {
      return;
    }

    multiSelectedExcerpts.value.push({
      start,
      end,
      text: trimmed,
    });

    isModalHidden.value = true;
    return;
  }

  offsetStart.value = start;
  offsetEnd.value = end;
  selectedText.value = trimmed;

  // Arrasto e duplo clique chegam aqui pelo mesmo selection-made, então os dois
  // seguem a mesma regra. O modo IA fica de fora: o AddAiNoteForm depende do
  // prompt digitado no modal, não há o que salvar sem ele.
  if (isQuickNoteMode.value && !isAiNoteMode.value) {
    isModalHidden.value = true;
    await nextTick();
    await addNoteFormRef.value?.quickSubmit();
    return;
  }

  isModalHidden.value = false;
}

function handleBulkAddNotesOpened() {
  isMultiSelectMode.value = false;
}

// Atalho do rodapé do mini-modal de trechos. NoteSection é irmão (ambos vivem em
// RightArea.vue), então a ponte é o mesmo padrão de CustomEvent já usado aqui:
// quem abre o BulkAddNoteForm continua sendo NoteSection.openBulkAddModal(),
// exatamente como no item "Criar notas em lote" do menu de ações.
function handleRequestBulkAdd() {
  // Mesmo efeito colateral do menu, que dispara "note-action-clicked" antes de
  // emitir "bulk-add" (desliga modo IA e esconde o modal de seleção simples).
  handleNoteActionClicked();
  window.dispatchEvent(new CustomEvent("request-bulk-add-notes"));
}

function handleClearMultiSelectedExcerptsEvent() {
  isMultiSelectMode.value = false;
  multiSelectedExcerpts.value = [];
  isModalHidden.value = true;
  selectedText.value = "";
  offsetStart.value = null;
  offsetEnd.value = null;
}

onMounted(() => {
  hasHighlights.value = true;
  hasExponent.value = false;

  window.addEventListener("bulk-add-notes-opened", handleBulkAddNotesOpened);
  window.addEventListener(
    "clear-multi-selected-excerpts",
    handleClearMultiSelectedExcerptsEvent
  );
  window.addEventListener("note-action-clicked", handleNoteActionClicked);
  document.addEventListener("click", handleProgressClickOutside);

  refreshNoteVisibilityObserver();
});

// Reconecta o observador sempre que a árvore de spans é refeita. As três causas:
// as notas mudaram (filtro, criação, exclusão), o conteúdo mudou, ou o editor
// entrou/saiu do lugar do renderizador. E o próprio interruptor, que quando volta
// a ligar precisa reobservar do zero.
watch(
  [
    () => noteStore.notesForHighlight,
    () => textStore.fileContent,
    () => isEditingContent.value,
    () => noteStore.followReading,
  ],
  () => refreshNoteVisibilityObserver()
);

// ── Fólio ligado por padrão em texto importado de PDF ───────────────────────
//
// O botão nasce desligado no store, e é o certo para a base inteira: num DOCX
// ele abre uma faixa na margem que só interessa a quem vai citar, e num texto
// colado não há o que mostrar.
//
// O PDF é o caso em que ele já tem o que mostrar ao abrir: a página imprime o
// próprio fólio, a importação o preserva na linha do título corrente, e é essa
// linha que o botão passou a governar. Deixá-lo desligado esconderia, por
// padrão, um conteúdo que veio do arquivo — daí ligá-lo quando a marca está lá.
//
// UMA VEZ POR TEXTO, e não a cada mudança de conteúdo: depois disso a escolha é
// do usuário, e salvar uma edição não pode reacender o botão que ele apagou.
const pageMarksDecidedFor = ref(null);

watch(
  () => [textStore.selectedText?.textId, textStore.fileContent],
  ([textId, content]) => {
    if (!textId || textId === pageMarksDecidedFor.value) return;
    if (typeof content !== "string" || content.length === 0) return;

    pageMarksDecidedFor.value = textId;

    if (content.includes(RUNNING_HEAD_CLASS)) noteStore.setPageMarksMode(true);
  },
  { immediate: true }
);

onUnmounted(() => {
  stopNoteVisibilityObserver();
});

onUnmounted(() => {
  document.removeEventListener("click", handleProgressClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  window.removeEventListener("bulk-add-notes-opened", handleBulkAddNotesOpened);
  window.removeEventListener(
    "clear-multi-selected-excerpts",
    handleClearMultiSelectedExcerptsEvent
  );
  window.removeEventListener("note-action-clicked", handleNoteActionClicked);
});

function toggleExponent() {
  hasExponent.value = !hasExponent.value;
}
function toggleHighlights() {
  hasHighlights.value = !hasHighlights.value;
}
function togglePageMarks() {
  hasPageMarks.value = !hasPageMarks.value;
}
function toggleCreatorsStack() {
  hasCreatorsStack.value = !hasCreatorsStack.value;
}

function toggleMultiSelect() {
  const newValue = !isMultiSelectMode.value;
  isMultiSelectMode.value = newValue;

  if (newValue) {
    isModalHidden.value = true;
    isAiNoteMode.value = false;
  } else {
    multiSelectedExcerpts.value = [];
  }
}

function handleMiniModalClose() {
  isMultiSelectMode.value = false;
  multiSelectedExcerpts.value = [];
}

// "Limpar" do rodapé: esvazia a pilha mas mantém o modo múltiplo ligado, para
// o usuário recomeçar a seleção sem reativar o modo.
function handleClearExcerpts() {
  multiSelectedExcerpts.value = [];
}

function handleRemoveExcerpt(excerptToRemove) {
  if (!excerptToRemove) return;

  const idx = multiSelectedExcerpts.value.indexOf(excerptToRemove);
  if (idx !== -1) {
    multiSelectedExcerpts.value.splice(idx, 1);
    return;
  }

  multiSelectedExcerpts.value = multiSelectedExcerpts.value.filter(
    (ex) =>
      ex.start !== excerptToRemove.start ||
      ex.end !== excerptToRemove.end ||
      ex.text !== excerptToRemove.text
  );
}

const isAiNoteMode = ref(false);
function toggleAiNoteMode() {
  isAiNoteMode.value = !isAiNoteMode.value;
  isModalHidden.value = true;

  window.dispatchEvent(
    new CustomEvent("ai-note-mode-changed", {
      detail: { isAiNoteMode: isAiNoteMode.value },
    })
  );
}

function handleNoteActionClicked() {
  isAiNoteMode.value = false;
  isModalHidden.value = true;

  window.dispatchEvent(
    new CustomEvent("ai-note-mode-changed", {
      detail: { isAiNoteMode: false },
    })
  );
}

// ── Progresso da atividade (dropdown no cabeçalho) ───────────────────────────
// Mesmo vocabulário e mesmo visual do dropdown por linha do ActivityForm, aqui
// aplicado à atividade aberta — quem está lendo altera o progresso sem voltar
// para a lista.
const { canEditActivity: memberCanEditActivity } = useMemberRestrictions();

const isProgressMenuOpen = ref(false);
const isProgressUpdating = ref(false);

// selectedActivity.userPermissions traz as permissões EFETIVAS do usuário atual
// (o backend devolve a lista completa para o criador), o mesmo dado que o
// ActivityForm usa na coluna Progresso.
function hasActivityPermission(activity, permissionName) {
  if (!activity || !Array.isArray(activity.userPermissions)) return false;
  const target = permissionName.toLowerCase();
  return activity.userPermissions.some(
    (perm) => perm.permissionName?.toLowerCase() === target
  );
}

const isActivityCreator = computed(() => {
  const userId = userStore.currentUser?.userId;
  return (
    userId != null && userId === activityStore.selectedActivity?.createdByUserId
  );
});

// Vínculo (portão do membro gerenciado) AND criador OU permissão de editar.
const canEditActivityProgress = computed(
  () =>
    memberCanEditActivity.value &&
    (isActivityCreator.value ||
      hasActivityPermission(activityStore.selectedActivity, "Editar"))
);

// Mesmo contrato do ActivityForm: enquanto a atividade está planejada (início
// previsto sem início efetivo), o modo se sobrepõe ao status persistido — que
// segue "Em andamento" — no ícone, no tooltip e na marcação do menu.
const displayedProgress = computed(() =>
  activityPlanningProgress(activityStore.selectedActivity)
);

const displayedProgressIcon = computed(
  () =>
    displayedProgress.value?.icon ||
    progressIcon(activityStore.selectedActivity?.status)
);

const displayedProgressColorClass = computed(
  () =>
    displayedProgress.value?.colorClass ||
    progressColorClass(activityStore.selectedActivity?.status)
);

const displayedProgressLabel = computed(
  () =>
    displayedProgress.value?.label ||
    progressFullLabel(activityStore.selectedActivity?.status)
);

function isProgressOptionCurrent(option) {
  if (option.value === PLANNED_PROGRESS_ACTION.value) {
    return Boolean(displayedProgress.value);
  }
  return (
    !displayedProgress.value &&
    isSameProgress(activityStore.selectedActivity?.status, option.value)
  );
}

const showEditActivityModal = ref(false);
const openEditInPlanningMode = ref(false);

// "Planejada" não é gravável daqui — as datas previstas precisam ser digitadas,
// então a entrada abre o formulário de edição em modo planejamento.
function handleProgressOption(option) {
  if (option.value !== PLANNED_PROGRESS_ACTION.value) {
    handleProgressChange(option.value);
    return;
  }

  isProgressMenuOpen.value = false;
  openEditInPlanningMode.value = true;
  showEditActivityModal.value = true;
}

function closeEditActivityModal() {
  showEditActivityModal.value = false;
  openEditInPlanningMode.value = false;
}

// O PUT do formulário troca selectedActivity pela resposta, que não traz
// allNotes nem userPermissions; getActivityById devolve o estado completo de
// que esta tela depende — o mesmo cuidado do handleProgressChange.
async function handleActivityEdited() {
  const activityId =
    activityStore.selectedActivity?.activityId ?? activityStore.idActivity;
  closeEditActivityModal();
  if (activityId) await activityStore.getActivityById(activityId);
}

// Repartição do total de notas. Mesmo padrão do menu de progresso: um estado, um
// toggle e o fechamento por clique fora, registrado no mesmo listener.
const isNotesCountMenuOpen = ref(false);

function toggleNotesCountMenu() {
  isNotesCountMenuOpen.value = !isNotesCountMenuOpen.value;
}

function toggleProgressMenu() {
  if (isProgressUpdating.value) return;
  isProgressMenuOpen.value = !isProgressMenuOpen.value;
}

function handleProgressClickOutside(event) {
  if (
    isProgressMenuOpen.value &&
    !event.target.closest(".progress-menu-container")
  ) {
    isProgressMenuOpen.value = false;
  }

  if (
    isNotesCountMenuOpen.value &&
    !event.target.closest(".notes-count-container")
  ) {
    isNotesCountMenuOpen.value = false;
  }
}

async function handleProgressChange(newStatus) {
  const activity = activityStore.selectedActivity;
  if (!activity) return;

  isProgressMenuOpen.value = false;
  // Planejada é um modo, não um status: mesmo escolhendo o status que já está
  // gravado, o clique tem efeito — inicia a atividade.
  const planningState = displayedProgress.value;
  if (isSameProgress(activity.status, newStatus) && !planningState) return;

  const activityId = activity.activityId;
  isProgressUpdating.value = true;
  try {
    // O PUT reescreve a atividade inteira: sem reenviar as permissões atuais,
    // o compartilhamento seria apagado.
    const rawPerms = await activityStore.fetchActivityPermissions(activityId);
    const permissions = (rawPerms || []).map((p) => ({
      entityId: p.userId != null ? p.userId : p.groupId,
      permissionId: p.permissionId,
      type: p.userId != null ? "user" : "group",
    }));

    // "Concluída" exige Data de Fim: se ainda não houver, atribui a data/hora
    // atual no mesmo formato datetime-local usado no EditActivityForm.
    let endedAt = activity.endedAt ?? null;
    if (newStatus === "Concluída" && !endedAt) {
      endedAt = formatDateTimeForInput(new Date());
    }

    await activityStore.updateActivity({
      activityId,
      title: activity.title,
      description: activity.description,
      status: newStatus,
      active: activity.active,
      // Escolher um status numa atividade planejada é iniciá-la de fato; as
      // previstas seguem no payload como histórico. Omiti-las do PUT apagaria
      // as colunas, porque o backend atribui o DTO inteiro sem checar nulo.
      startedAt: planningState
        ? new Date().toISOString()
        : activity.startedAt ?? null,
      endedAt,
      plannedStartAt: activity.plannedStartAt ?? null,
      plannedEndAt: activity.plannedEndAt ?? null,
      textId: activity.textId,
      permissions,
    });

    // updateActivity troca selectedActivity pela resposta do PUT, que não traz
    // allNotes nem userPermissions; getActivityById devolve o estado completo
    // de que esta tela depende.
    await activityStore.getActivityById(activityId);

    showSuccessToast(
      `Progresso alterado para "${progressFullLabel(newStatus)}" com sucesso!`
    );
  } catch (error) {
    console.error("❌ Falha ao atualizar o progresso da atividade:", error);
    showErrorToast(
      error?.response?.data?.message ||
        error?.message ||
        "Ocorreu um erro ao atualizar o progresso."
    );
  } finally {
    isProgressUpdating.value = false;
  }
}

// ── Toast ────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("error");
const toastTimeoutId = ref(null);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function goBackToActivities() {
  router.push({
    name: "ActivityForm",
    params: {
      textId: textStore.idText,
      textTitle: textStore.selectedText?.title ?? "",
    },
  });
}

function goToTexts() {
  router.push({ name: "TextForm" });
}

// ── delete via menu de contexto do TextWithNotes ─────────────────────────────
const showDeleteNoteModal = ref(false);
const noteIdToDelete = ref(null);

function openDeleteNoteModal(noteId) {
  noteIdToDelete.value = noteId;
  showDeleteNoteModal.value = true;
}

function handleDeleteModalClose() {
  showDeleteNoteModal.value = false;
  noteIdToDelete.value = null;
}
</script>

<template>
  <div class="text-displayer-root">
    <AddNoteForm
      v-if="!isAiNoteMode"
      ref="addNoteFormRef"
      @close="isModalHidden = true"
      :activityId="activityStore.idActivity"
      :textRangeStart="offsetStart"
      :textRangeEnd="offsetEnd"
      :createdByUserId="activityStore.selectedActivity.createdByUserId"
    />
    <AddAiNoteForm
      v-else
      @close="isModalHidden = true"
      :activityId="activityStore.idActivity"
      :textRangeStart="offsetStart"
      :textRangeEnd="offsetEnd"
      :createdByUserId="activityStore.selectedActivity.createdByUserId"
    />

    <Teleport to="body">
      <NoteCardModal
        v-if="isNoteModalVisible"
        :show="isNoteModalVisible"
        :noteId="selectedNoteId"
        :userId="userStore.currentUser?.userId"
        @close="isNoteModalVisible = false"
      />

      <TextCardModal
        v-if="showTextCardModal"
        :show="showTextCardModal"
        :textId="textStore.idText"
        @close="closeTextCard"
        @text-updated="handleTextUpdated"
      />

      <AuthorCardModal
        v-if="showAuthorCardModal"
        :show="showAuthorCardModal"
        :authorId="selectedAuthorId"
        @close="closeAuthorCard"
      />

      <UserCardModal
        v-if="showUserCardModal && selectedUserId"
        :show="showUserCardModal"
        :userId="selectedUserId"
        @close="handleUserCardModalClose"
      />

      <ActivityCardModal
        v-if="showActivityCardModal"
        :show="showActivityCardModal"
        :activityId="selectedActivityId"
        @close="closeActivityCard"
      />

      <EditActivityForm
        v-if="
          showEditActivityModal &&
          canEditActivityProgress &&
          activityStore.selectedActivity?.activityId
        "
        :activityId="activityStore.selectedActivity.activityId"
        :textId="activityStore.selectedActivity.textId ?? textStore.idText"
        :start-in-planning-mode="openEditInPlanningMode"
        @close="closeEditActivityModal"
        @refresh="handleActivityEdited"
      />

      <CategoryCardModal
        v-if="showCategoryCardModal && selectedCategoryId"
        :show="showCategoryCardModal"
        :categoryId="selectedCategoryId"
        @close="closeCategoryCard"
      />

      <TopicCardModal
        v-if="showTopicCardModal && selectedTopicId"
        :show="showTopicCardModal"
        :topicId="selectedTopicId"
        @close="closeTopicCard"
      />

      <!-- Cadastro de autor, gênero e assunto chamado de dentro dos seletores
           do painel. São os MESMOS formulários que o resto do sistema usa; o
           que muda é só de onde são abertos. -->
      <div v-if="showAddAuthorModal" class="fixed inset-0 z-[10050]">
        <AddAuthorForm
          :initialName="newEntityName"
          @author-added="handleAuthorAdded"
          @close="showAddAuthorModal = false"
        />
      </div>

      <div v-if="showAddCategoryModal" class="fixed inset-0 z-[10050]">
        <AddCategoryForm
          :initialName="newEntityName"
          @category-added="handleCategoryAdded"
          @close="showAddCategoryModal = false"
        />
      </div>

      <div v-if="showAddTopicModal" class="fixed inset-0 z-[10050]">
        <AddTopicForm
          :initialName="newEntityName"
          @topic-added="handleTopicAdded"
          @close="showAddTopicModal = false"
        />
      </div>

      <!-- Destinos do menu ••• do painel. São os MESMOS componentes que a
           estante abre pelo menu dos cards; o que muda é de onde vêm. -->
      <PermissionView
        v-if="showTextPermissionModal"
        :show="showTextPermissionModal"
        entity-type="text"
        :text-id="textStore.selectedText?.textId"
        title="Permissões do texto"
        @close="showTextPermissionModal = false"
      />

      <EditTextForm
        v-if="showEditTextModal"
        :textId="textStore.selectedText?.textId"
        @close="handleEditTextModalClose"
        @text-updated="handleTextUpdatedFromModal"
      />

      <DeleteNoteForm
        v-if="showDeleteNoteModal"
        :noteId="noteIdToDelete"
        @close="handleDeleteModalClose"
      />
    </Teleport>

    <ExcerptMultiSelectMiniModal
      :show="isMultiSelectMode"
      :excerpts="multiSelectedExcerpts"
      :activityCreatedByUserId="activityStore.selectedActivity?.createdByUserId"
      @close="handleMiniModalClose"
      @remove-excerpt="handleRemoveExcerpt"
      @bulk-add="handleRequestBulkAdd"
      @clear-excerpts="handleClearExcerpts"
    />

    <!-- ✅ CABEÇALHO: "Atividade" (esq.) + cards (centro-dir.) + botões (dir.) -->
    <div class="reader-header">
      <div class="reader-header-bar">
        <span class="reader-header-label">Atividade</span>

        <div class="reader-header-actions">
          <!-- Navegação: Textos / Atividades -->
          <div class="compact-controls" aria-label="Navegação">
            <button
              type="button"
              class="icon-btn"
              @click="goToTexts"
              title="Ir para textos"
              aria-label="Ir para textos"
            >
              <i class="fas fa-book nav-icon"></i>
            </button>

            <button
              type="button"
              class="icon-btn"
              @click="goBackToActivities"
              title="Ir para atividades"
              aria-label="Ir para atividades"
            >
              <i class="fas fa-tasks nav-icon"></i>
            </button>

            <!-- Progresso da atividade — só para o criador ou quem pode editar -->
            <div v-if="canEditActivityProgress" class="progress-menu-container">
              <button
                type="button"
                class="icon-btn"
                :class="{ active: isProgressMenuOpen }"
                :disabled="isProgressUpdating"
                @click.stop="toggleProgressMenu"
                :title="`Progresso: ${displayedProgressLabel} — clique para alterar`"
                aria-label="Alterar progresso da atividade"
                :aria-expanded="isProgressMenuOpen"
              >
                <i
                  :class="[
 'fas',
                    displayedProgressIcon,
                    displayedProgressColorClass,
                    'nav-icon',
                    'progress-status-icon',
                  ]"
                ></i>
                <i
                  class="fas fa-chevron-down progress-chevron"
                  :class="{ open: isProgressMenuOpen }"
                ></i>
              </button>

              <div v-if="isProgressMenuOpen" class="progress-dropdown-menu">
                <ul>
                  <li
                    v-for="opt in ACTIVITY_PROGRESS_MENU_OPTIONS"
                    :key="opt.value"
                    :class="[
 'progress-option',
                      progressMenuColorClass(opt),
                      isProgressOptionCurrent(opt)
                        ? 'progress-option--current'
                        : '',
                    ]"
                    @click="handleProgressOption(opt)"
                    :title="opt.label"
                  >
                    <span class="progress-option-label">
                      <i
                        :class="[
 'fas',
                          opt.icon,
                          progressMenuColorClass(opt),
                          'progress-status-icon',
                          'progress-option-icon',
                        ]"
                      ></i>
                      {{ opt.short }}
                    </span>
                    <i
                      v-if="isProgressOptionCurrent(opt)"
                      class="fas fa-check progress-check-icon"
                    ></i>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <span class="stats-divider" aria-hidden="true"></span>

          <!--
            Uma chip só, com o total, que abre a repartição ao clique. As chips
            de "minhas" e "de outros" saíram do cabeçalho: os três números
            competiam pelo mesmo espaço e só o total é consultado o tempo todo —
            os outros dois são pergunta ocasional, e pergunta ocasional cabe num
            dropdown.
          -->
          <div class="header-stats">
            <div class="notes-count-container">
              <button
                type="button"
                class="stat-chip stat-chip--total stat-chip--button"
                :class="{ open: isNotesCountMenuOpen }"
                @click.stop="toggleNotesCountMenu"
                title="Total de notas — clique para ver a repartição"
                aria-label="Total de notas"
                :aria-expanded="isNotesCountMenuOpen"
              >
                <!-- `fa-sticky-note` é o ícone que a navegação já usa para
                     "Notas" (DashboardLayout). O `fa-layer-group` anterior dizia
                     "camadas", que não é o que a chip conta. -->
                <i class="fas fa-sticky-note"></i>{{ noteStore.allNotes.length }}
                <i
                  class="fas fa-chevron-down stat-chip-chevron"
                  :class="{ open: isNotesCountMenuOpen }"
                ></i>
              </button>

              <div v-if="isNotesCountMenuOpen" class="notes-count-dropdown">
                <!--
                  As avulsas entram INDENTADAS sob cada autoria, e não como linha
                  irmã: elas são subconjunto — uma nota avulsa minha já está
                  contada em "Minhas notas". Lidas como irmãs, os números não
                  fechariam com o total da chip.

                  Só aparecem quando há alguma; ramo vazio é ruído.
                -->
                <ul>
                  <li class="notes-count-row">
                    <span class="notes-count-label">
                      <i class="fas fa-user notes-count-icon notes-count-icon--mine"></i>
                      Minhas notas
                    </span>
                    <span class="notes-count-value">{{ noteStore.myNotesCount }}</span>
                  </li>
                  <li
                    v-if="noteStore.myDetachedNotesCount > 0"
                    class="notes-count-row notes-count-row--child"
                  >
                    <span class="notes-count-label">avulsas</span>
                    <span class="notes-count-value">
                      {{ noteStore.myDetachedNotesCount }}
                    </span>
                  </li>

                  <li class="notes-count-row">
                    <span class="notes-count-label">
                      <i class="fas fa-users notes-count-icon notes-count-icon--others"></i>
                      Notas de outros
                    </span>
                    <span class="notes-count-value">{{ noteStore.commentsCount }}</span>
                  </li>
                  <li
                    v-if="noteStore.othersDetachedNotesCount > 0"
                    class="notes-count-row notes-count-row--child"
                  >
                    <span class="notes-count-label">avulsas</span>
                    <span class="notes-count-value">
                      {{ noteStore.othersDetachedNotesCount }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <span class="stats-divider" aria-hidden="true"></span>

          <div class="compact-controls" aria-label="Controles de exibição">
            <button
              type="button"
              class="icon-btn"
              :class="{ active: hasExponent }"
              @click="toggleExponent"
              title="Alternar exibição de expoentes"
              aria-label="Alternar expoentes"
            >
              <span class="icon-a">A</span><sup class="icon-sup">1</sup>
            </button>

            <button
              type="button"
              class="icon-btn"
              :class="{ active: hasCreatorsStack }"
              @click="toggleCreatorsStack"
              title="Alternar exibição dos criadores de notas"
              aria-label="Alternar exibição dos criadores de notas"
            >
              <i class="fas fa-user-circle"></i>
            </button>

            <button
              type="button"
              class="icon-btn"
              :class="{ active: hasHighlights }"
              @click="toggleHighlights"
              title="Alternar exibição de marcações"
              aria-label="Alternar marcações"
            >
              <i class="fas fa-highlighter"></i>
            </button>

            <!-- Fólio: a página do documento original, na margem. Só faz efeito
                 em texto importado de arquivo paginado — nos demais o botão
                 alterna um estado que não tem o que mostrar, e é por isso que
                 ele nasce desligado. -->
            <button
              type="button"
              class="icon-btn"
              :class="{ active: hasPageMarks }"
              @click="togglePageMarks"
              title="Alternar exibição da página do original"
              aria-label="Alternar página do original"
            >
              <i class="fas fa-bookmark"></i>
            </button>

            <!--
              Seguir a leitura: com ele ligado, a lista à direita mostra as notas
              do trecho que está na tela, e a paginação some porque não há o que
              percorrer. Desligado, a lista volta a ser a paginada.

              Vive aqui, e não na coluna de notas, porque é um controle de
              LEITURA: o que ele acompanha é a rolagem do texto.
            -->
            <button
              type="button"
              class="icon-btn follow-reading-toggle"
              :class="{ active: noteStore.followReading }"
              @click="noteStore.setFollowReading(!noteStore.followReading)"
              :title="
                noteStore.followReading
                  ? 'Seguindo a leitura: a lista mostra as notas do trecho na tela'
                  : 'Seguir a leitura: mostrar as notas do trecho que está na tela'
              "
              aria-label="Alternar seguir a leitura"
              :aria-pressed="noteStore.followReading"
            >
              <i class="fas fa-book-reader"></i>
            </button>

            <!-- Bloqueado pelo vínculo: continua na barra, esmaecido e inerte. -->
            <button
              type="button"
              class="icon-btn quick-note-toggle"
              :class="{
 active: isQuickNoteMode,
                'icon-btn--blocked': !memberCanCreateNote,
              }"
              :disabled="!memberCanCreateNote"
              @click="toggleQuickNoteMode"
              :title="
                memberCanCreateNote
                  ? 'Alternar criação rápida de nota ao selecionar (sem formulário)'
                  : blockedByMembershipHint
              "
              aria-label="Alternar criação rápida de nota ao selecionar"
            >
              <i class="fas fa-bolt"></i>
            </button>

            <button
              type="button"
              class="icon-btn multi-select-toggle"
              :class="{
 active: isMultiSelectMode,
                'icon-btn--blocked': !memberCanCreateNote,
              }"
              :disabled="!memberCanCreateNote"
              @click="toggleMultiSelect"
              :title="
                memberCanCreateNote
                  ? 'Alternar seleção múltipla de trechos'
                  : blockedByMembershipHint
              "
              aria-label="Alternar seleção múltipla de trechos"
            >
              <i class="fas fa-i-cursor"></i>
            </button>

            <button
              v-if="userStore.currentUser?.userName === 'Sys'"
              type="button"
              class="icon-btn ai-note-toggle"
              :class="{ active: isAiNoteMode }"
              :disabled="isMultiSelectMode"
              @click="toggleAiNoteMode"
              title="Alternar criação de nota com IA"
              aria-label="Alternar criação de nota com IA"
            >
              <i class="fas fa-robot"></i>
            </button>

            <!--
              Conversão do conteúdo para HTML. Aparece para Sys ou para quem criou
              o texto — mesma regra de canEditContent — e só enquanto o texto ainda
              for plano — convertido, não há o que converter.

              ⚠️ Esconder o botão NÃO é a autorização: quem barra é o endpoint
              (TextHtmlConversionController), que replica esta mesma regra no
              servidor. Aqui é só para não poluir a interface de quem não pode usar.
              E a ação é irreversível fora do backup, por isso o modal mostra o
              ensaio antes de qualquer gravação.
            -->
            <button
              v-if="canEditContent && isPlaintextContent"
              type="button"
              class="icon-btn"
              @click="showConvertModal = true"
              title="Converter conteúdo para HTML (mostra o ensaio antes)"
              aria-label="Converter conteúdo para HTML"
            >
              <i class="fas fa-code"></i>
            </button>
          </div>
        </div>
      </div>

      <hr class="reader-header-rule" />
    </div>

    <!-- ✅ ROLAGEM GERAL — carrega .text-scroll-container para o auto-scroll do TextWithNotes -->
    <div
      ref="readerScrollEl"
      class="reader-scroll text-scroll-container"
      :class="{ 'reader-scroll--editing': isEditingContent }"
    >
      <!--
        ✅ Pilha de metadados — some durante a edição, para o editor abrir no topo.
        Ele passa a ter rolagem própria, então esta pilha empurraria a caixa para
        fora da tela em vez de rolar junto.
      -->
      <div v-if="!isEditingContent" class="activity-meta">
        <!-- 1) Série de anotações (atividade) — seletor acima do card do texto -->
        <SeriesSwitcher @open-activity-card="openActivityCard" />

        <!-- 2) Card do TEXTO — agrupa título, subtítulo, temas e campos do texto.
             Sem faixa de título: a capa e o nome do texto já dizem o que o card
             é, e o `aria-label` continua nomeando a seção para quem não a vê. -->
        <section class="text-meta-card" aria-label="Detalhes do texto">
          <!--
            Menu do texto, no canto superior direito do painel. Os lápis dos
            campos resolvem a edição miúda ali mesmo; aqui ficam as três coisas
            que pedem a tela inteira: o card do texto, quem tem acesso a ele e o
            formulário completo.
          -->
          <div v-if="!isEditingMeta" class="text-menu-container">
            <button
              type="button"
              class="text-menu-btn"
              :class="{ active: isTextMenuOpen }"
              title="Opções do texto"
              aria-label="Opções do texto"
              :aria-expanded="isTextMenuOpen"
              @click.stop="toggleTextMenu"
            >
              <i class="fas fa-ellipsis-h"></i>
            </button>

            <div v-if="isTextMenuOpen" class="text-dropdown-menu">
              <ul>
                <li @click="openTextDetailsFromMenu">
                  <!-- Mesmo olho do "Ver nota" do menu das notas: ver é ver. -->
                  <i class="fas fa-eye"></i>
                  <span>Ver detalhes do texto</span>
                </li>
                <li @click="openTextPermissions">
                  <i class="fas fa-user-shield"></i>
                  <span>Ver permissões</span>
                </li>
                <!-- Editar só para quem pode: é a mesma regra dos lápis, e o
                     backend a repete. Esconder aqui é para não prometer o que
                     o servidor recusaria. -->
                <li v-if="canEditMeta" @click="openEditTextModal">
                  <i class="fas fa-pencil-alt"></i>
                  <span>Editar texto</span>
                </li>
              </ul>
            </div>
          </div>

          <!--
            Capa à esquerda, metadados à direita — a mesma leitura da estante
            (TextCardsForm.vue), para que o texto seja reconhecido pela capa
            também aqui dentro, e não só na prateleira.
          -->
          <div class="text-meta-body">
            <!--
              Capa e o seu lápis, na coluna da esquerda. O lápis fica ABAIXO da
              capa, e não sobre ela: sobre a imagem ele taparia justamente o que
              se está olhando, e numa capa desenhada cairia em cima do título.

              A capa inteira some durante a edição — o painel é estreito, e o
              formulário precisa da largura do card para os seletores de autor,
              gênero e assuntos caberem sem espremer.
            -->
            <div v-if="!isEditingMeta" class="meta-cover-field meta-field">
              <!-- Capa enviada pelo usuário. Já chega recortada em 3:4 pelo
                   utils/coverImage.js; `object-fit: cover` é rede para capa
                   antiga ou vinda por outro caminho. -->
              <img
                v-if="coverUrl"
                class="meta-cover meta-cover--image"
                :src="coverUrl"
                :alt="`Capa de ${textStore.selectedText.title}`"
                loading="lazy"
                decoding="async"
              />

              <!-- Sem capa enviada: a desenhada, que sempre existe. -->
              <div
                v-else
                class="meta-cover"
                :style="coverStyle(textStore.selectedText)"
                aria-hidden="true"
              >
                <span class="cover-title">{{
                  toTitleCase(textStore.selectedText.title)
                }}</span>
                <span
                  v-if="textStore.selectedText.authorName"
                  class="cover-author"
                >
                  {{ toTitleCase(textStore.selectedText.authorName) }}
                </span>
                <span class="cover-tombo">{{
                  coverTombo(textStore.selectedText)
                }}</span>
              </div>

              <button
                v-if="canEditMeta"
                type="button"
                class="meta-field-edit meta-cover-edit-btn"
                title="Editar capa"
                aria-label="Editar capa"
                @click.stop="startMetaEdit('cover')"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>
            </div>

            <div class="text-meta-main">
              <!--
                Painel em edição: o formulário ocupa o lugar dos campos lidos.
                Todos os seis aparecem, e não só o do lápis clicado — assim dá
                para preencher um subtítulo ou uma fonte que ainda não existem
                e que, por não existirem, não teriam onde ser clicados.

                Gravar é atômico: o PUT do texto é substituição, e a store relê
                o texto e as permissões antes de mandar (ver `updateTextFields`).
              -->
              <!-- Sem `@keydown.esc` aqui: quem trata o Esc é o ouvinte de
                   janela (handleMetaEditKeydown), que responde com o foco em
                   qualquer lugar, e não só dentro do formulário. -->
              <form
                v-if="isEditingMeta"
                class="meta-edit"
                @submit.prevent="saveMetaEdit"
              >
                <!-- Capa. O recorte 3:4 e o redimensionamento acontecem no
                     navegador (utils/coverImage.js), como no cadastro do texto:
                     o que sobe já é a imagem final, e não a foto de 8MB. -->
                <label class="meta-edit-label">Capa</label>
                <div class="meta-cover-edit">
                  <img
                    v-if="coverThumbUrl"
                    class="meta-cover-thumb"
                    :src="coverThumbUrl"
                    alt="Capa do texto"
                  />
                  <!-- Sem imagem, a miniatura mostra a capa DESENHADA — a
                       mesma que o painel exibe, e a mesma que o texto vai ter
                       se nada for enviado. Um ícone genérico no lugar dela
                       daria a entender que o texto ficaria sem capa, e capa
                       sempre existe. Vale também depois de "Remover": ali ela
                       é a previsão do que sobra. -->
                  <div
                    v-else
                    class="meta-cover meta-cover-thumb--drawn"
                    :style="coverStyle(textStore.selectedText)"
                    aria-hidden="true"
                  >
                    <span class="cover-title">{{
                      toTitleCase(textStore.selectedText.title)
                    }}</span>
                    <span
                      v-if="textStore.selectedText.authorName"
                      class="cover-author"
                    >
                      {{ toTitleCase(textStore.selectedText.authorName) }}
                    </span>
                    <span class="cover-tombo">{{
                      coverTombo(textStore.selectedText)
                    }}</span>
                  </div>

                  <div class="meta-cover-actions">
                    <label class="meta-edit-btn meta-edit-btn--ghost">
                      {{ isProcessingCover ? "Processando..." : "Escolher imagem" }}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        class="meta-cover-input"
                        :disabled="isSavingMeta || isProcessingCover"
                        @change="onCoverChange"
                      />
                    </label>
                    <button
                      v-if="coverThumbUrl"
                      type="button"
                      class="meta-edit-btn meta-edit-btn--ghost"
                      :disabled="isSavingMeta || isProcessingCover"
                      @click="clearCoverEdit"
                    >
                      Remover
                    </button>
                  </div>

                  <p v-if="coverError" class="meta-edit-error">
                    <i class="fas fa-exclamation-circle"></i>
                    <span>{{ coverError }}</span>
                  </p>
                </div>

                <label class="meta-edit-label" for="meta-title">Título</label>
                <input
                  id="meta-title"
                  :ref="setMetaRef('title')"
                  v-model="metaDraft.title"
                  type="text"
                  class="meta-edit-input"
                  placeholder="Digite o título do texto"
                  :disabled="isSavingMeta"
                />

                <label class="meta-edit-label" for="meta-subtitle">
                  Subtítulo
                </label>
                <input
                  id="meta-subtitle"
                  :ref="setMetaRef('subtitle')"
                  v-model="metaDraft.subtitle"
                  type="text"
                  class="meta-edit-input"
                  placeholder="Digite o subtítulo do texto"
                  :disabled="isSavingMeta"
                />

                <!-- O Enter para nos seletores: dentro deles ele ESCOLHE, e se
                     subisse até o form gravaria no mesmo gesto — escolher um
                     assunto fecharia o painel salvo. Nos campos de texto o
                     Enter continua salvando, que é o esperado. -->
                <label class="meta-edit-label">Autor</label>
                <div @keydown.enter.stop>
                  <Multiselect
                    v-model="metaDraft.author"
                    :taggable="true"
                    :options="authorOptions"
                    :loading="isLoadingMetaOptions"
                    :allow-empty="true"
                    label="label"
                    track-by="value"
                    placeholder="Selecione ou crie um autor"
                    tag-placeholder="Tecle enter para criar este autor"
                    @tag="startAuthorCreation"
                    select-label="Tecle enter para selecionar"
                    deselect-label="Tecle enter para remover"
                    selected-label="Selecionado"
                    :disabled="isSavingMeta"
                  />
                </div>

                <label class="meta-edit-label">Gênero</label>
                <div @keydown.enter.stop>
                  <Multiselect
                    v-model="metaDraft.category"
                    :taggable="true"
                    :options="categoryOptions"
                    :loading="isLoadingMetaOptions"
                    :allow-empty="true"
                    label="label"
                    track-by="value"
                    placeholder="Selecione ou crie um gênero"
                    tag-placeholder="Tecle enter para criar este gênero"
                    @tag="startCategoryCreation"
                    select-label="Tecle enter para selecionar"
                    deselect-label="Tecle enter para remover"
                    selected-label="Selecionado"
                    :disabled="isSavingMeta"
                  />
                </div>

                <label class="meta-edit-label">Assuntos</label>
                <div @keydown.enter.stop>
                  <Multiselect
                    v-model="metaDraft.topics"
                    :taggable="true"
                    :options="topicOptions"
                    :multiple="true"
                    :loading="isLoadingMetaOptions"
                    :allow-empty="true"
                    label="label"
                    track-by="value"
                    placeholder="Selecione ou crie um assunto"
                    tag-placeholder="Tecle enter para criar este assunto"
                    @tag="startTopicCreation"
                    select-label="Tecle enter para selecionar"
                    deselect-label="Tecle enter para remover"
                    selected-label="Selecionado"
                    :disabled="isSavingMeta"
                  />
                </div>

                <label class="meta-edit-label" for="meta-source">Fonte</label>
                <input
                  id="meta-source"
                  :ref="setMetaRef('source')"
                  v-model="metaDraft.source"
                  type="text"
                  class="meta-edit-input"
                  placeholder="Digite a fonte do texto"
                  :disabled="isSavingMeta"
                />

                <p v-if="metaError" class="meta-edit-error" role="alert">
                  <i class="fas fa-exclamation-circle"></i>
                  <span>{{ metaError }}</span>
                </p>

                <div class="meta-edit-actions">
                  <button
                    type="button"
                    class="meta-edit-btn meta-edit-btn--ghost"
                    :disabled="isSavingMeta"
                    @click="cancelMetaEdit"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    class="meta-edit-btn meta-edit-btn--primary"
                    :disabled="isSavingMeta"
                  >
                    <i class="fas fa-save"></i>
                    <span>{{ isSavingMeta ? "Salvando..." : "Salvar" }}</span>
                  </button>
                </div>
              </form>

              <!--
                Painel em leitura. Cada campo ganha um lápis que aparece com o
                mouse NAQUELE campo — o mesmo gesto dos cards de nota. O clique
                nos campos continua fazendo o que fazia: o título abre o card do
                texto, a chip abre o card do gênero ou do assunto. Quem edita é
                o lápis.
              -->
              <template v-else>
                <div class="text-title-group">
                  <div class="meta-field">
                    <button
                      type="button"
                      class="text-name link-btn"
                      :title="textStore.selectedText.title"
                      @click="openTextCard"
                    >
                      {{ textStore.selectedText.title }}
                    </button>
                    <button
                      v-if="canEditMeta"
                      type="button"
                      class="meta-field-edit"
                      title="Editar título"
                      aria-label="Editar título"
                      @click.stop="startMetaEdit('title')"
                    >
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                  </div>

                  <!-- Subtítulo ausente vira frase, e não some: sem ela não
                       haveria onde clicar para escrever o primeiro. Some de vez
                       para quem não pode editar — aí é linha sem função. -->
                  <div
                    v-if="textStore.selectedText.subtitle || canEditMeta"
                    class="meta-field"
                  >
                    <div
                      class="text-subtitle"
                      :class="{
 'text-subtitle--empty': !textStore.selectedText.subtitle,
                      }"
                    >
                      {{
                        textStore.selectedText.subtitle ||
                        "subtítulo não informado"
                      }}
                    </div>
                    <button
                      v-if="canEditMeta"
                      type="button"
                      class="meta-field-edit"
                      title="Editar subtítulo"
                      aria-label="Editar subtítulo"
                      @click.stop="startMetaEdit('subtitle')"
                    >
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                  </div>

                  <!-- Autor. Clicável só quando há card para abrir; sem autor
                       catalogado, o rótulo diz QUAL campo falta — "Não
                       informado" solto embaixo do título poderia ser qualquer
                       coisa. -->
                  <div class="meta-field">
                    <button
                      v-if="textStore.selectedText.authorId"
                      type="button"
                      class="text-author link-btn"
                      :title="`Abrir autor: ${textStore.selectedText.authorName}`"
                      @click="openAuthorCard(textStore.selectedText?.authorId)"
                    >
                      {{ catalogLabel(textStore.selectedText.authorName, "Autor") }}
                    </button>
                    <p v-else class="text-author text-author--empty">
                      {{ catalogLabel(null, "Autor") }}
                    </p>
                    <button
                      v-if="canEditMeta"
                      type="button"
                      class="meta-field-edit"
                      title="Editar autor"
                      aria-label="Editar autor"
                      @click.stop="startMetaEdit('author')"
                    >
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                  </div>
                </div>

                <!--
                  Gênero e assuntos na MESMA fileira, cada um com o seu lápis.
                  São campos diferentes — um lápis só para os dois não diria
                  qual deles ele abre —, mas ocupam a mesma linha porque são a
                  classificação do texto e se leem juntos. O que separa um do
                  outro é o grupo: o lápis mora dentro do grupo do seu campo, e
                  aparece com o mouse NAQUELE grupo, não na fileira inteira.

                  Campo vazio não ganha lápis: a própria chip de ausência abre a
                  edição. Dois alvos para a mesma coisa, num espaço deste
                  tamanho, seriam um a mais — e o rótulo já diz o que falta.
                -->
                <div class="topic-chips">
                  <span class="topic-chip-group meta-field">
                    <template v-for="(chip, idx) in genreChips" :key="`g-${idx}`">
                      <button
                        v-if="isLinkedChip(chip)"
                        type="button"
                        class="topic-chip"
                        :class="`topic-chip--${chip.kind}`"
                        :title="chipTitle(chip)"
                        @click="openChipCard(chip)"
                      >
                        {{ chipText(chip) }}
                      </button>
                      <!-- Desabilitada para quem não pode editar: a lacuna
                           continua sendo informação sobre o texto; o que some é
                           o convite a resolvê-la. -->
                      <button
                        v-else-if="chip.kind === 'empty'"
                        type="button"
                        class="topic-chip topic-chip--empty"
                        :disabled="!canEditMeta"
                        :title="canEditMeta ? 'Informar o gênero' : null"
                        @click.stop="startMetaEdit('category')"
                      >
                        {{ chipText(chip) }}
                      </button>
                      <span
                        v-else
                        class="topic-chip"
                        :class="`topic-chip--${chip.kind}`"
                        :title="chipTitle(chip)"
                      >
                        {{ chipText(chip) }}
                      </span>
                    </template>
                    <button
                      v-if="canEditMeta && hasGenreInfo"
                      type="button"
                      class="meta-field-edit"
                      title="Editar gênero"
                      aria-label="Editar gênero"
                      @click.stop="startMetaEdit('category')"
                    >
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                  </span>

                  <span class="topic-chip-group meta-field">
                    <template
                      v-for="(chip, idx) in topicChipList"
                      :key="`t-${idx}`"
                    >
                      <button
                        v-if="isLinkedChip(chip)"
                        type="button"
                        class="topic-chip"
                        :class="`topic-chip--${chip.kind}`"
                        :title="chipTitle(chip)"
                        @click="openChipCard(chip)"
                      >
                        {{ chipText(chip) }}
                      </button>
                      <button
                        v-else-if="chip.kind === 'empty'"
                        type="button"
                        class="topic-chip topic-chip--empty"
                        :disabled="!canEditMeta"
                        :title="canEditMeta ? 'Informar os assuntos' : null"
                        @click.stop="startMetaEdit('topics')"
                      >
                        {{ chipText(chip) }}
                      </button>
                      <span
                        v-else
                        class="topic-chip"
                        :class="`topic-chip--${chip.kind}`"
                        :title="chipTitle(chip)"
                      >
                        {{ chipText(chip) }}
                      </span>
                    </template>
                    <button
                      v-if="canEditMeta && hasTopicsInfo"
                      type="button"
                      class="meta-field-edit"
                      title="Editar assuntos"
                      aria-label="Editar assuntos"
                      @click.stop="startMetaEdit('topics')"
                    >
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                  </span>
                </div>

                <!-- Fonte, em fileira só dela e logo acima do tamanho. Com
                     fonte, a chip é só rótulo (não há card de fonte a abrir) e
                     quem edita é o lápis; sem fonte, a própria chip abre a
                     edição, com o cursor já no campo. -->
                <div class="topic-chips topic-chips--source meta-field">
                  <button
                    v-if="!hasSourceInfo"
                    type="button"
                    class="topic-chip topic-chip--empty"
                    :disabled="!canEditMeta"
                    :title="canEditMeta ? 'Informar a fonte' : null"
                    @click.stop="startMetaEdit('source')"
                  >
                    {{ chipText(metaSourceChip) }}
                  </button>
                  <template v-else>
                    <span
                      class="topic-chip"
                      :class="`topic-chip--${metaSourceChip.kind}`"
                      :title="chipTitle(metaSourceChip)"
                    >
                      {{ chipText(metaSourceChip) }}
                    </span>
                    <button
                      v-if="canEditMeta"
                      type="button"
                      class="meta-field-edit"
                      title="Editar fonte"
                      aria-label="Editar fonte"
                      @click.stop="startMetaEdit('source')"
                    >
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                  </template>
                </div>
              </template>

              <!--
                Rodapé do card: tamanho à esquerda, criador à direita, na mesma
                linha miúda e apagada. Fica fora do formulário porque não é
                editável — os dois são registro, e não catalogação.
              -->
              <div class="meta-foot">
                <span class="meta-foot-size" :title="pageEquivalenceHint">
                  {{ textLengthLabel }}
                </span>

                <!-- Criador: AVATAR + "Criado por {nome}, em {data}". A linha
                     inteira é o botão porque avatar e nome deixaram de ser
                     vizinhos — o rótulo entrou entre os dois —, e dois botões
                     para a mesma pessoa seriam dois alvos para o mesmo card. -->
                <button
                  type="button"
                  class="inserted-by-link"
                  :title="`Ver ${textStore.selectedText.createdByUserName || 'usuário'}`"
                  @click.stop="openUserCardModal(textStore.selectedText?.createdByUserId)"
                >
                  <span class="inserted-by-avatar">
                    <img
                      v-if="creatorAvatarUrl"
                      :src="creatorAvatarUrl"
                      :alt="textStore.selectedText.createdByUserName || 'Usuário'"
                    />
                    <i v-else class="far fa-user"></i>
                  </span>
                  <span class="inserted-by-prefix">Criado por</span>
                  <!-- Nome e data num item só: o `gap` do flex abriria um vão
                       antes da vírgula se a data fosse item separado. -->
                  <span class="inserted-by-who"
                    ><span class="inserted-by-name">{{
                      textStore.selectedText.createdByUserName || "—"
                    }}</span>{{ createdAtSuffix }}</span
                  >
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- ✅ "folha" de leitura (sem scroll próprio; o scroll é o .reader-scroll) -->
      <div class="text-card">
        <!--
          `mouseover` no contêiner, e não em cada span: delegação. Os spans de
          nota são centenas, e — o que mais importa — os DOIS renderizadores já
          os marcam com `data-note-ids`. Ouvir aqui atende os dois sem duplicar
          código, que é o defeito que o documento adverte enquanto eles
          coexistirem.
        -->
        <div
          class="text-reading-area"
          :class="{ 'text-reading-area--editing': isEditingContent }"
          @mouseover="onReadingAreaMouseover"
        >
          <!--
            Ações de conteúdo — copiar, editar e exportar —, no canto superior
            direito da área de leitura, atrás de um menu ⋯. Flutuam sobre o texto
            (altura zero no fluxo) e o `sticky` as mantém alcançáveis enquanto se
            rola um texto longo.
            A faixa aparece para todos: copiar e "Exportar texto" valem para
            qualquer usuário; editar continua só para quem pode editar o texto,
            mesma regra do TextCardModal: Sys ou quem criou.
          -->
          <div
            class="content-edit-actions"
            :class="{ 'content-edit-actions--editing': isEditingContent }"
          >
            <template v-if="!isEditingContent">
              <!--
                Mesmo botão e mesmo menu do canto do card de metadados
                (.text-menu-btn / .text-dropdown-menu): é o mesmo gesto - abrir
                as opções daquilo que se está vendo -, e dois desenhos para o
                mesmo gesto fariam pensar em coisas diferentes.
              -->
              <div ref="contentMenuEl" class="content-menu-container">
                <button
                  type="button"
                  class="text-menu-btn"
                  :class="{ active: isContentMenuOpen }"
                  title="Ações do texto"
                  aria-label="Ações do texto"
                  aria-haspopup="menu"
                  :aria-expanded="isContentMenuOpen"
                  @click.stop="isContentMenuOpen = !isContentMenuOpen"
                >
                  <i class="fas fa-ellipsis-h"></i>
                </button>

                <div v-if="isContentMenuOpen" class="text-dropdown-menu">
                  <ul>
                    <li
                      v-if="canEditContent"
                      @click.prevent.stop="startContentEditFromMenu"
                    >
                      <!-- Mesmo lápis dos campos do painel: editar é editar, e
                           dois desenhos para o mesmo verbo na mesma tela fariam
                           pensar que são coisas diferentes. -->
                      <i class="fas fa-pencil-alt"></i>
                      <span>Editar</span>
                    </li>
                    <li @click.prevent.stop="copyContentFromMenu">
                      <i class="far fa-copy"></i>
                      <span>Copiar</span>
                    </li>
                    <li @click.prevent.stop="openPrintOptions">
                      <i class="fas fa-download"></i>
                      <span>Exportar texto</span>
                    </li>
                  </ul>
                </div>
              </div>
            </template>

            <template v-else>
              <button
                type="button"
                class="content-edit-btn content-edit-btn--primary"
                :disabled="isSavingContent"
                title="Salvar alterações"
                @click="saveContentEdit"
              >
                <i class="fas fa-save"></i>
                {{ isSavingContent ? "Salvando…" : "Salvar" }}
              </button>
              <button
                type="button"
                class="content-edit-btn"
                :disabled="isSavingContent"
                title="Descartar alterações"
                @click="cancelContentEdit"
              >
                <i class="fas fa-times"></i>
              </button>
            </template>
          </div>

          <p v-if="contentEditError" class="content-edit-error">
            {{ contentEditError }}
          </p>

          <!--
            Modo EDIÇÃO: a mesma área vira editável, com o componente que
            corresponde ao formato — editor formatado em HTML, caixa de texto em
            texto puro. Abrir o editor rich text sobre conteúdo plano converteria
            o texto sem ensaio; por isso cada formato tem o seu.
          -->
          <template v-if="isEditingContent">
            <!--
              Sem nenhuma das variáveis de sobreposição da barra: aqui o editor é
              uma CAIXA, e a barra dele ocupa a altura que tem, como em qualquer
              formulário. Quem rola é o próprio editor.

              `min-height="0"` entrega a altura à cadeia flexível. O padrão do
              componente é 18rem, que num visor baixo viraria um piso maior que a
              caixa — e, como o scroller externo não rola durante a edição, o
              rodapé com o contador ficaria recortado.
            -->
            <RichTextEditor
              v-if="!isPlaintextContent"
              ref="richTextEditorEl"
              v-model="editableContent"
              :disabled="isSavingContent"
              min-height="0"
              placeholder="Edite o conteúdo do texto…"
            />
            <CustomTextarea
              v-else
              v-model:body="editableContent"
              :disabled="isSavingContent"
              class="content-edit-textarea"
              :unwrapPastedLineBreaks="true"
            />
          </template>

          <!--
            Dois renderizadores, um por formato. Recebem as MESMAS props e emitem
            os MESMOS eventos, então só muda qual é montado e o invólucro.

            O <p class="text-body"> com white-space: pre-wrap sobrevive apenas no
            ramo de texto puro: é ele que hoje transforma os \n em quebra visível.
            No ramo HTML seria desastre — renderizaria literalmente a indentação do
            fonte, e <p> dentro de <p> faria o navegador remontar a árvore.
          -->
          <p v-else-if="isPlaintextContent" class="text-body">
            <TextWithNotes
              v-if="userStore.currentUser"
              :content="textStore.fileContent"
              :notes="noteStore.notesForHighlight"
              :userId="userStore.currentUser?.userId"
              :createdByUserId="textStore.selectedText?.createdByUserId"
              :updatedByUserId="textStore.selectedText?.updatedByUserId"
              :hasExponent="hasExponent"
              :hasHighlights="hasHighlights"
              :isMultiSelectMode="isMultiSelectMode"
              @selection-made="onSelectionMade"
              @note-clicked="onNoteClicked"
              @notes-focused="onNotesFocused"
              @note-details="onNoteDetails"
              @request-delete="openDeleteNoteModal"
            />
          </p>

          <div v-else class="text-body text-body--html">
            <TextWithNotesHtml
              v-if="userStore.currentUser"
              :content="textStore.fileContent"
              :notes="noteStore.notesForHighlight"
              :userId="userStore.currentUser?.userId"
              :createdByUserId="textStore.selectedText?.createdByUserId"
              :updatedByUserId="textStore.selectedText?.updatedByUserId"
              :hasExponent="hasExponent"
              :hasHighlights="hasHighlights"
              :hasPageMarks="hasPageMarks"
              :isMultiSelectMode="isMultiSelectMode"
              @selection-made="onSelectionMade"
              @note-clicked="onNoteClicked"
              @notes-focused="onNotesFocused"
              @note-details="onNoteDetails"
              @request-delete="openDeleteNoteModal"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Ensaio + confirmação da conversão do conteúdo para HTML (só Sys) -->
    <ConvertTextToHtmlModal
      :show="showConvertModal"
      :textId="textStore.idText"
      @close="showConvertModal = false"
      @converted="handleTextConverted"
    />

    <!-- Opções de impressão: aberto pelo "Exportar texto" do menu ⋯ da folha. -->
    <PrintNotes :show="showPrintOptions" @close="showPrintOptions = false" />

    <!-- Toast -->
    <Teleport to="body">
      <transition name="toast-fade">
        <div
          v-if="showToast"
          class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
        >
          <div
            :class="[
 'toast pointer-events-auto',
              toastType === 'error' ? 'toast-error' : 'toast-success',
            ]"
          >
            <div class="toast-icon">
              <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
                <path
                  v-if="toastType === 'error'"
                  d="M10 5v6M10 13.5v1.5"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                />
                <path
                  v-else
                  d="M7.5 10.5 9.5 12.5 13 8.5"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="toast-content">
              <p class="toast-title">
                {{ toastType === "error" ? "Erro" : "Sucesso!" }}
              </p>
              <p class="toast-message">{{ toastMessage }}</p>
            </div>
            <button
              type="button"
              class="toast-close hover:bg-black/10 rounded-full p-1 transition"
              @click="showToast = false"
              aria-label="Fechar notificação"
            >
              &times;
            </button>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<style scoped>
.text-displayer-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border-radius: 16px;
  background: var(--bg-header);
  /* confina a rolagem ao .reader-scroll (o root em si não rola) */
  overflow: hidden;
}

/* ===== Cabeçalho "Atividade" + cards + botões + régua (fixo) ===== */
.reader-header {
  flex-shrink: 0;
  /* o dropdown de progresso é ancorado aqui dentro e avança sobre a folha de
     leitura — o cabeçalho precisa formar contexto de empilhamento acima dela */
  position: relative;
  z-index: 20;
}

.reader-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.reader-header-label {
  /* alinha a borda esquerda com o título (main-title-link tem padding-left: 8px) */
  padding-left: 8px;
  font-size: 1.3rem; /* equivalente a h2 */
  font-weight: 700;
  color: #000;
  text-align: left;
}

.reader-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.reader-header-rule {
  border: none;
  border-top: 1px solid var(--border-color);
  /* laterais negativas anulam o padding (8px) do root → linha full-width */
  margin: 6px -8px 12px;
}

/* ===== Cards reduzidos (chips de estatística) ===== */
.header-stats {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px; /* < altura do rótulo 1.5rem → não aumenta o cabeçalho */
  padding: 0 9px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid transparent;
  cursor: default;
}

.stat-chip i {
  font-size: 0.72rem;
  opacity: 0.85;
}

.stat-chip--total {
  background-color: #ece7ff;
  color: #4b3f86;
  border-color: #ded5ff;
}

/* A chip do total virou botão. `cursor: default` do .stat-chip existe porque as
   outras eram só rótulo; esta é clicável e precisa dizê-lo. */
.stat-chip--button {
  cursor: pointer;
  font-family: inherit;
}

.stat-chip--button:hover,
.stat-chip--button.open {
  background-color: #ded5ff;
}

.stat-chip-chevron {
  font-size: 0.55rem;
  opacity: 0.7;
  transition: transform 0.2s ease-in-out;
}

.stat-chip-chevron.open {
  transform: rotate(180deg);
}

/* ===== Repartição do total (dropdown da chip) ===== */
.notes-count-container {
  position: relative;
  display: inline-flex;
}

.notes-count-dropdown {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: calc(100% + 4px);
  z-index: 100;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
  min-width: 190px;
}

.notes-count-dropdown ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}

.notes-count-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.4rem 0.6rem;
  font-size: 0.82rem;
  color: var(--text-dark);
  white-space: nowrap;
}

.notes-count-label {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.notes-count-icon {
  font-size: 0.75rem;
}

/* As cores que as chips removidas carregavam sobrevivem aqui, nos ícones: eram
   elas que distinguiam "minhas" de "de outros" à primeira vista. */
.notes-count-icon--mine {
  color: #4f46e5;
}

.notes-count-icon--others {
  color: #b45309;
}

.notes-count-value {
  font-weight: 700;
  color: var(--text-dark);
}

/* Ramo da árvore: o "└" desenhado por pseudo-elemento, não por caractere no
   texto — assim ele não é lido em voz alta nem entra numa cópia do conteúdo.
   O recuo alinha o rótulo filho com o texto do pai, que começa depois do ícone. */
.notes-count-row--child {
  padding-top: 0.1rem;
  padding-bottom: 0.1rem;
  padding-left: 1.5rem;
  font-size: 0.78rem;
  color: var(--text-muted);
  position: relative;
}

.notes-count-row--child::before {
  content: "└";
  position: absolute;
  left: 0.75rem;
  top: 0.02rem;
  color: var(--neutral-400);
  font-size: 0.78rem;
  line-height: 1.4;
}

/* Peso normal no filho: o número do pai é o que se lê primeiro, e dois pesos
   iguais fariam os dois competirem. */
.notes-count-row--child .notes-count-value {
  font-weight: 600;
  color: var(--text-light);
}

/* ===== Rolagem geral (abaixo do cabeçalho). Também é o .text-scroll-container ===== */
.reader-scroll {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: calc(100% + 8px);
  max-width: calc(100% + 8px);
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  /* avança só para a direita: scrollbar encostada no limite do container */
  margin-right: -8px;
  padding-right: 8px;
  box-sizing: border-box;
}

/* ===== Separador visual stats ↔ botões ===== */
.stats-divider {
  display: inline-block;
  width: 1px;
  height: 18px;
  background: var(--border-color);
  border-radius: 1px;
  align-self: center;
  flex-shrink: 0;
}

/* ===== Pilha de metadados ===== */
.activity-meta {
  display: flex;
  flex-direction: column;
  gap: 0;
  text-align: left;
  flex-shrink: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Nome da atividade (título principal) */
.main-title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.15;
  margin: 0 0 16px; /* espaço generoso abaixo separa do card do texto */
  text-align: left;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.main-title-link {
  font: inherit;
  line-height: inherit;
  padding: 2px 8px;
  margin-left: -8px;
  justify-content: flex-start;
  text-align: left;
  white-space: normal;
}

.main-title-link .meta-text {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}

.meta-hover-link.main-title-link:hover {
  background-color: transparent;
  transform: none;
}

.main-title-link:hover .meta-text {
  text-decoration: underline;
}

/* ── Card do texto: agrupa tudo que pertence ao texto ── */
.text-meta-card {
  /* Bloco de contenção do menu ••• , que é posicionado no canto. */
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 14px 18px 16px;
  border: 1px solid rgba(202, 138, 4, 0.22);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.32);
  box-shadow: 0 2px 8px rgba(120, 53, 15, 0.06);
}

/* ── Menu ••• do painel ──────────────────────────────────────────────────────
   Ancorado no canto do card, fora do fluxo: no fluxo, ele seria mais uma linha
   e empurraria a capa e os metadados para baixo. */
.text-menu-container {
  position: absolute;
  top: 8px;
  right: 10px;
  z-index: 20;
}

/* O mesmo menu, no canto da folha de leitura. Aqui quem põe o botão no lugar é
   a faixa sticky (.content-edit-actions); ao contêiner só cabe ancorar o painel,
   que abre abaixo e para a esquerda, como no card de metadados.

   `align-self: flex-start` é o que faz o painel cair ABAIXO do botão: a faixa
   tem altura zero (para não empurrar o texto), e um item flex esticado herdaria
   essa altura — o `top: 100%` do painel passaria a medir do topo do botão, que é
   onde ele aparecia por cima dele. Com o alinhamento ao início, o contêiner
   volta a ter a altura do botão, e 100% é a base dele. */
.content-menu-container {
  position: relative;
  align-self: flex-start;
}

/* Aqui o painel tem a largura da linha mais larga, e não o mínimo de 200px do
   menu do card: os rótulos são curtos ("Editar", "Copiar", "Exportar texto") e
   o mínimo deixava uma faixa vazia à direita deles. O `nowrap` das linhas já
   garante que `max-content` meça a maior delas inteira. */
.content-menu-container .text-dropdown-menu {
  min-width: 0;
  width: max-content;
}

/* Mesma aparência do ••• dos cards de nota (.btn-icon-sm.btn-icon-neutral em
   NoteList.vue): cinza claro, cantos arredondados, escurecendo sob o mouse.
   É o mesmo gesto nas duas telas — abrir as opções daquilo que se está vendo —,
   e dois desenhos para o mesmo gesto fariam pensar em coisas diferentes. */
.text-menu-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 9px;
  /* Sem fundo em repouso: o painel já é uma caixa clara, e uma segunda caixa
     no canto dela pesava. O fundo entra sob o ponteiro, que é quando ele tem
     função — dizer onde o clique cai. */
  background-color: transparent;
  color: var(--text-dark);
  font-size: 0.78rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.text-menu-btn:hover,
.text-menu-btn.active {
  background-color: var(--border-color);
}

.text-menu-btn:active {
  transform: translateY(1px);
}

.text-menu-btn:focus,
.text-menu-btn:focus-visible {
  outline: none;
}

.text-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 100;
  min-width: 200px;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
}

.text-dropdown-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}

.text-dropdown-menu li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.82rem;
  color: var(--text-dark);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease, transform 0.05s ease;
}

/* Mesmo cinza do menu ••• das notas (.row-dropdown-menu li:hover). */
.text-dropdown-menu li:hover {
  background-color: var(--surface-hover);
}

.text-dropdown-menu li:active {
  transform: translateY(1px);
}

/* Largura fixa alinha os rótulos, mesmo com ícones de larguras diferentes.
   A cor é a mesma do botão que abre o menu — cinza-chumbo —, para o menu ler
   como uma coisa só. */
.text-dropdown-menu li i {
  width: 0.95rem;
  text-align: center;
  color: var(--text-dark);
}

/* A primeira linha dos metadados abre espaço para o botão: sem isso, um título
   longo passaria por baixo dele, e o lápis do título disputaria o mesmo canto. */
.text-title-group > .meta-field:first-child {
  padding-right: 26px;
}

/* ── Corpo do card: capa à esquerda, metadados à direita ─────────────────── */
/* `align-items: center` centra a capa VERTICALMENTE na coluna de metadados, que
   é sempre a mais alta das duas. Centrar sem esticar é o que `center` faz e
   `stretch` (o padrão) não faria: a capa tem proporção fixa, e esticada
   apareceria deformada ou com faixa vazia. */
.text-meta-body {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* Coluna da direita: título, subtítulo, autor, chips, campos e criador — a
   mesma sequência do corpo do card na estante. O `min-width: 0` não é enfeite:
   sem ele um título sem espaços estica a coluna e empurra a capa para fora do
   card, porque item de flex não encolhe abaixo do próprio conteúdo. */
.text-meta-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

/* ── Capa ─────────────────────────────────────────────────────────────────────
   A mesma capa da estante, num corpo menor: aqui o painel é estreito e a capa
   divide a largura com os metadados. A PROPORÇÃO é que não muda — 3:4, a mesma
   do recorte feito em utils/coverImage.js e a mesma do `.card-cover`; largura e
   altura saem de uma variável só para não poderem divergir.
   `align-items: flex-start` no pai mantém a capa do tamanho que ela pede, em
   vez de esticá-la quando os metadados forem mais altos. */
.meta-cover {
  --cover-w: 96px;

  position: relative;
  flex: 0 0 auto;
  width: var(--cover-w);
  height: calc(var(--cover-w) * 4 / 3);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.6rem 0.5rem;
  border-radius: 8px;
  color: #fff;
  text-align: center;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
}

/* A capa enviada ocupa a mesma caixa da desenhada, para o painel não mudar de
   forma conforme o texto tenha imagem ou não. */
.meta-cover--image {
  display: block;
  padding: 0;
  object-fit: cover;
  background: var(--neutral-200);
}

/* Vinheta: profundidade sem precisar de uma segunda cor por paleta. */
.meta-cover:not(.meta-cover--image)::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at 50% 15%,
    rgba(255, 255, 255, 0.18),
    transparent 65%
  );
  pointer-events: none;
}

.cover-title {
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.25;
  /* Título longo é cortado: a capa é reconhecimento visual, e o título
     completo está logo ao lado, no campo do texto. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.cover-author {
  font-size: 0.52rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.85;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* Tombo: encostado na base, como etiqueta de acervo. */
.cover-tombo {
  margin-top: auto;
  font-size: 0.54rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  opacity: 0.75;
}

.text-title-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

/* Nome do texto.
   O seletor duplo NÃO é zelo: `.link-btn` traz `font: inherit` e
   `color: inherit` — a taquigrafia `font` zera peso e corpo junto — e está
   DEPOIS daqui na folha. Com a mesma especificidade, quem vem depois ganha, e
   era isso que vinha apagando o negrito e a cor deste título. Somar as duas
   classes coloca esta regra acima, onde quer que `.link-btn` esteja. */
.text-name.link-btn {
  align-self: flex-start;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-dark);
}

/* Subtítulo */
.text-subtitle {
  font-size: 0.82rem;
  color: var(--text-light);
  font-style: italic;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* ── Lápis por campo do painel ───────────────────────────────────────────────
   Mesmo gesto dos cards de nota: o lápis fica na linha do campo, logo depois do
   conteúdo, e aparece com o mouse NAQUELE campo — apontar um e ver o lápis de
   todos não diria qual deles o clique abre.

   Some por `opacity`, e não por `display`: estando no fluxo, sumir de verdade
   faria a linha se reescrever a cada entrada e saída do mouse. */
.meta-field {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
}

/* Sem isto, um título sem espaços não encolhe e empurra o lápis para fora do
   painel: item de flex não encolhe abaixo do próprio conteúdo. */
.meta-field > :not(.meta-field-edit) {
  min-width: 0;
}

.meta-field-edit {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: rgba(202, 138, 4, 0.08);
  color: #b08434;
  font-size: 0.62rem;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

.meta-field:hover .meta-field-edit,
.meta-field-edit:focus-visible {
  opacity: 1;
}

/* Nas fileiras de chips o lápis não RESERVA espaço: escondido por `opacity`,
   ele deixava um vão largo entre a chip de gênero e as de assunto, que é onde
   ele mora. Aqui ele nasce e morre com `display`, e ao aparecer empurra as
   chips seguintes para a direita — o vão passa a existir só quando há botão
   nele. Nos outros campos o lápis fica no FIM da linha, onde reservar o espaço
   não separa nada e evita que a linha se reescreva a cada passagem do mouse. */
.topic-chip-group .meta-field-edit {
  display: none;
}

.topic-chip-group:hover .meta-field-edit,
.topic-chip-group .meta-field-edit:focus-visible {
  display: inline-flex;
}

/* Teclado não tem hover: sem a regra acima, quem navega por Tab focaria um
   botão invisível. */
.meta-field-edit:focus-visible {
  outline: 2px solid rgba(202, 138, 4, 0.35);
  outline-offset: 1px;
}

.meta-field-edit:hover {
  background: rgba(202, 138, 4, 0.16);
  color: #8a6420;
}

/* Subtítulo ausente: itálico apagado, como as demais lacunas do painel. */
.text-subtitle--empty {
  opacity: 0.65;
}

/* Gênero e assunto dividem a fileira, mas cada um é um grupo com o seu lápis:
   é o grupo, e não a fileira, que responde ao mouse. */
.topic-chip-group {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

/* Capa e o lápis dela, empilhados: o botão fica embaixo da imagem. */
.meta-cover-field {
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

/* Sem `margin-left`: aqui o lápis está sozinho na linha de baixo, e não
   encostado no fim de um texto. */
.meta-cover-edit-btn {
  margin-left: 0;
}

/* ── Campo da capa, dentro do formulário ─────────────────────────────────── */
.meta-cover-edit {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/* Miniatura na mesma proporção de livro do resto do sistema (3:4), pequena: é
   conferência do que foi escolhido, não exibição. */
.meta-cover-thumb {
  flex: 0 0 auto;
  /* Proporção de livro (3:4), a mesma do recorte feito em utils/coverImage.js e
     a mesma da capa exibida ao lado dos metadados: a miniatura mostra o que vai
     ser gravado, e outra proporção mentiria sobre o corte. */
  width: 72px;
  height: 96px;
  border-radius: 6px;
  object-fit: cover;
  background: #f1ece2;
}

/* A capa desenhada na miniatura é a MESMA `.meta-cover` do painel — mesmo
   gradiente, mesmo título, mesmo tombo. O que muda é só o tamanho, e ele muda
   pela variável de onde a própria regra tira largura e altura: assim a
   proporção 3:4 vem de graça e não há um segundo lugar dizendo quanto mede uma
   capa. */
.meta-cover-thumb--drawn {
  --cover-w: 72px;

  border-radius: 6px;
}

.meta-cover-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* O <input type="file"> vive dentro do <label> que faz as vezes de botão: o
   controle nativo não aceita a aparência dos outros botões do formulário, e
   escondê-lo dentro do rótulo mantém o clique funcionando sem JavaScript. */
.meta-cover-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.meta-cover-edit label.meta-edit-btn {
  position: relative;
  cursor: pointer;
}

/* ── Formulário no painel ────────────────────────────────────────────────── */
.meta-edit {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;
}

.meta-edit-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #a08a5b;
  margin-top: 4px;
}

.meta-edit-input {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid rgba(202, 138, 4, 0.28);
  border-radius: 8px;
  background: var(--surface);
  font-family: inherit;
  font-size: 0.82rem;
  color: var(--text-dark);
}

.meta-edit-input:focus {
  outline: none;
  border-color: #c0842f;
  box-shadow: 0 0 0 3px rgba(202, 138, 4, 0.16);
}

.meta-edit-input:disabled {
  background: #faf8f4;
  color: #a8a29e;
}

.meta-edit-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 0 0;
  font-size: 0.74rem;
  color: var(--danger-text);
}

.meta-edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.meta-edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.meta-edit-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.meta-edit-btn--ghost {
  background: var(--surface);
  border-color: rgba(202, 138, 4, 0.28);
  color: #8a6420;
}

.meta-edit-btn--ghost:hover:not(:disabled) {
  background: #fdfaf4;
}

/* Mesmo par do botão de criar do AddTextForm.vue: roxo claro com letra quase
   preta. É a cor de "gravar" da casa, e vale para os três botões de salvar do
   leitor — o do painel, o do conteúdo e o das notas. */
.meta-edit-btn--primary {
  background: var(--brand);
  color: var(--text-on-brand);
}

.meta-edit-btn--primary:hover:not(:disabled) {
  background: var(--brand-hover);
}

/* O Multiselect traz o próprio CSS, num corpo maior que o do painel. Alinha com
   os outros campos do formulário. As cores das mensagens da lista (verde para o
   que adiciona, vermelho para o que remove) ficam com a biblioteca — é o mesmo
   par dos seletores do AddTextForm.vue. */
.meta-edit :deep(.multiselect) {
  min-height: 34px;
  font-size: 0.82rem;
}

.meta-edit :deep(.multiselect__tags) {
  min-height: 34px;
  padding: 5px 40px 0 8px;
  border-color: rgba(202, 138, 4, 0.28);
  border-radius: 8px;
}

.meta-edit :deep(.multiselect__tag) {
  background: rgba(202, 138, 4, 0.14);
  color: #8a6420;
  margin-bottom: 4px;
}

.meta-edit :deep(.multiselect__tag-icon:after) {
  color: #b08434;
}

.meta-edit :deep(.multiselect__tag-icon:hover) {
  background: rgba(202, 138, 4, 0.24);
}

/* Autor, logo abaixo do título (e do subtítulo, quando existe): é o segundo
   nome da lombada, como no card da estante. O cinza o põe um degrau abaixo do
   título, que é o nome que se procura primeiro.
   O seletor duplo tem o mesmo motivo do `.text-name.link-btn` acima: sem ele o
   `color: inherit` do `.link-btn` devolve o autor à cor do texto comum. */
.text-author,
.text-author.link-btn {
  margin: 0;
  align-self: flex-start;
  max-width: 100%;
  font-size: 0.8rem;
  color: var(--text-muted);
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Mesma linguagem da chip de ausência: itálico e apagado, para não competir
   com o nome de um autor de verdade. Sem card para abrir, não é botão. */
.text-author--empty {
  font-style: italic;
  color: var(--text-subtle);
}

/* ── Rodapé do card: tamanho à esquerda, criador à direita ───────────────────
   Uma linha só, na tipografia miúda e apagada do rodapé do card da estante.
   Não há régua acima: ela era o único traço horizontal de uma coluna estreita e
   chamava mais atenção para a separação do que para o que ela separa. O corpo
   miúdo, o cinza e o espaço já dizem que a linha mudou de assunto.
   A tipografia mora AQUI, e não no botão do criador: o `font: inherit` do botão
   a herda inteira, e um lugar só evita que as duas divirjam. */
.meta-foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 6px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  font-size: 0.68rem;
  color: var(--text-subtle);
}

.meta-foot-size {
  min-width: 0;
  overflow-wrap: anywhere;
}

.inserted-by-avatar {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--border-color);
  color: var(--text-light);
  font-size: 0.62rem;
}

.inserted-by-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.inserted-by-prefix {
  opacity: 0.75;
}

/* A linha inteira do rodapé é o botão → abre UserCardModal.
   `flex-wrap` porque a linha ("Criado por Fulano de Tal, em 12/03/2025") pode
   não caber na coluna, que agora divide a largura com a capa; quando quebra, a
   segunda linha também termina na margem direita, senão a quebra desfaria o
   alinhamento que o campo acabou de dar. */
.inserted-by-link {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 5px;
  /* Empurra o criador para a margem direita, e continua empurrando depois que
     a linha quebra — `space-between` no pai devolveria o botão ao início da
     segunda linha, desfazendo o alinhamento justamente quando a coluna está
     estreita, que é quando ele mais importa. */
  margin-left: auto;
  padding: 0;
  text-align: right;
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  border-radius: 999px;
  /* Repousa atenuado e acende sob o cursor: apagar no hover sinalizaria
     desativação, não interatividade. */
  opacity: 0.7;
  transition: opacity 0.15s ease;
}
.inserted-by-link:hover {
  opacity: 1;
}
.inserted-by-link:focus,
.inserted-by-link:focus-visible {
  outline: none;
  box-shadow: none;
}

/* Nome e data no MESMO item de flex: item separado ganharia o `gap` do botão, e
   a vírgula apareceria descolada do nome ("Fulano , em 12/03/2025"). */
.inserted-by-who {
  min-width: 0;
  overflow-wrap: anywhere;
}

.inserted-by-name {
  font-weight: 600;
  color: var(--text-muted);
}

/* ── Chips de gênero, assunto e fonte ────────────────────────────────────────
   As cores são as do card da estante, e a distinção entre elas é o ponto:
   gênero é a classificação ampla, assunto é o detalhe, fonte é a procedência.
   Uma fileira de chips todas iguais esconderia qual é qual. */
.topic-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
}

/* A fonte tem fileira só dela. A classe existe para dizer isso no markup: sem
   ela, um segundo .topic-chips solto pareceria repetição acidental. */
.topic-chips--source {
  margin-top: 2px;
}

.topic-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.5;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: left;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.05s ease;
}

.topic-chip--genre {
  background: var(--brand-soft);
  color: var(--text-on-brand-soft);
}

.topic-chip--topic {
  background: #e0f2fe;
  color: #0369a1;
}

/* Fonte: terceira cor, porque é uma terceira coisa — procedência, não
   classificação. Não é botão (não há card de fonte), então também não tem
   cursor nem realce. */
.topic-chip--source {
  background: var(--success-bg);
  color: var(--success-text);
  cursor: default;
}

/* Ausência: contorno tracejado e sem preenchimento, para ler como lacuna e não
   como mais uma classificação ao lado das de verdade. Não é botão — não há
   card a abrir —, então também não tem cursor nem realce. */
/* Ausência: contorno tracejado e sem preenchimento, para ler como lacuna e não
   como mais uma classificação. Ela é o próprio botão de editar o campo que
   falta — daí a mão; sem permissão de edição, o rótulo continua dito e só o
   convite some. */
.topic-chip--empty {
  background: transparent;
  border-color: var(--neutral-400);
  border-style: dashed;
  color: var(--text-subtle);
  font-weight: 500;
  font-style: italic;
  cursor: pointer;
}

.topic-chip--empty:disabled {
  cursor: default;
}

/* Sob o mouse muda só a LETRA, para o mesmo azul das chips de lacuna das notas
   (NoteList.vue). O tracejado cinza não acompanha: é ele que diz "aqui falta
   algo", e um contorno que muda de cor a cada passagem transformaria a lacuna
   em botão — o convite está no texto, e basta. */
.topic-chip--empty:not(:disabled):hover {
  color: #4f46e5;
}

.topic-chip--genre:hover {
  background-color: #ddd4fd;
}

.topic-chip--topic:hover {
  background-color: #cfe9fc;
}

.topic-chip:not(.topic-chip--empty):active {
  transform: translateY(1px);
}

/* ===== Links ===== */
.link-btn {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: left;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.link-btn:hover {
  text-decoration: underline;
}

/* Link com hover suave (usado no título da atividade) */
.meta-hover-link {
  border: none;
  background: transparent;
  padding: 2px 6px;
  margin: -2px 0;
  border-radius: 8px;
  cursor: pointer;
  color: inherit;
  transition: 0.2s;

  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  justify-content: flex-start;
  text-align: left;
}

.meta-hover-link:hover {
  background-color: var(--brand-tint);
  transform: translateY(-2px);
}

.meta-hover-link:active {
  transform: translateY(-1px);
}

.meta-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

/* ===== Controles compactos (no cabeçalho) ===== */
.compact-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 26px;
  min-width: 26px;
  padding: 0 8px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: var(--surface);
  cursor: pointer;
  color: var(--text-light);
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease, transform 0.05s ease;
}

/* Bloqueado pelo vínculo: visível, esmaecido e sem resposta ao hover. */
.icon-btn--blocked,
.icon-btn--blocked:hover {
  opacity: 0.45;
  cursor: not-allowed;
  background: var(--surface);
  transform: none;
}

.nav-icon {
  font-size: 0.7rem;
}

.icon-btn:hover {
  background-color: var(--neutral-100);
  color: var(--text-dark);
}

.icon-btn:active {
  transform: translateY(1px);
}

.icon-btn.active {
  background-color: var(--bg-active-nav);
  border-color: transparent;
  color: var(--text-dark);
  font-weight: 700;
}

.icon-btn.quick-note-toggle.active {
  background-color: var(--brand-light);
  border-color: transparent;
  color: var(--text-on-brand);
  font-weight: 700;
}

/* Roxo, como os demais interruptores de MODO (rápida, múltipla, IA). O
   `var(--bg-active-nav)` do `.active` padrão é pêssego e serve aos botões que
   apenas mostram/escondem algo; seguir a leitura muda o comportamento da coluna
   de notas, e essa distinção é a que a cor carrega neste cabeçalho. */
.icon-btn.follow-reading-toggle.active {
  background-color: var(--brand-light);
  border-color: transparent;
  color: var(--text-on-brand);
  font-weight: 700;
}

.icon-btn.multi-select-toggle.active {
  background-color: var(--brand-light);
  border-color: transparent;
  color: var(--text-on-brand);
  font-weight: 700;
}

.icon-btn.ai-note-toggle.active {
  background-color: var(--brand-light);
  border-color: transparent;
  color: #1a1a2e;
  font-weight: 700;
}

.icon-btn.ai-note-toggle:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

/* ── Progresso da atividade: botão + dropdown ───────────────────────────────
   Mesmo visual do dropdown por linha do ActivityForm (mesmo raio, sombra,
   opções neutras que só ganham a cor do status no hover e na opção atual). */
.progress-menu-container {
  position: relative;
  display: inline-flex;
}

/* gap maior só neste botão: o ícone de status e o chevron são dois símbolos,
   não um só. Os demais .icon-btn do cabeçalho seguem com o gap de 2px. */
.progress-menu-container .icon-btn {
  gap: 6px;
}

.progress-chevron {
  font-size: 0.6rem;
  opacity: 0.7;
  transition: transform 0.2s ease-in-out;
}
/* Aberto, o chevron aponta para cima — mesmo gesto do dropdown do ActivityForm */
.progress-chevron.open {
  transform: rotate(180deg);
}

.icon-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.progress-dropdown-menu {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: calc(100% + 4px);
  z-index: 100;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
  min-width: 175px;
}
.progress-dropdown-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}
.progress-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.82rem;
  color: var(--text-dark);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.progress-option:not(:hover):not(.progress-option--current) {
  background-color: transparent;
  color: var(--text-dark);
}
.progress-option:active {
  transform: translateY(1px);
}
.progress-option--current {
  font-weight: 700;
}
.progress-option-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
/* largura fixa evita que ícones de larguras diferentes desalinhem os rótulos */
.progress-option-icon {
  width: 0.95rem;
  text-align: center;
  font-size: 0.8rem;
}
.progress-check-icon {
  color: var(--brand-light);
  font-size: 0.75rem;
}

.status-red    { background-color: #ffc9c9; color: #c92a2a; }
.status-orange { background-color: #ffd8a8; color: #e8590c; }
.status-blue   { background-color: #a5d8ff; color: #1971c2; }
.status-green  { background-color: #b2f2bb; color: #2b8a3e; }
.status-yellow { background-color: #fff3bf; color: #f59f00; }
/* Modo planejado — mesma paleta do ActivityForm; aqui só o ícone do gatilho as
   usa, já que o menu lista apenas os status do fluxo. */
.status-future    { background-color: var(--brand-soft); color: #6d28d9; }
.status-due-today { background-color: var(--info-border); color: var(--link-hover); }
.status-overdue   { background-color: var(--danger-bg); color: var(--danger-text); }

/* Depois do bloco acima de propósito: o ícone fica só com a COR do status,
   sem o fundo em pílula que as mesmas classes dão ao badge do ActivityForm. */
.progress-status-icon {
  background-color: transparent;
}

.icon-a {
  font-size: 0.9rem;
  line-height: 1;
}

.icon-sup {
  font-size: 0.65rem;
  line-height: 1;
  transform: translateY(2px);
}

/* ===== "folha" de leitura ===== */
.text-card {
  background-color: var(--bg-main);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
  padding-top: 12px;
}

/* wrapper interno do texto (renomeado de .text-scroll-container p/ NÃO ser
   confundido com o scroller pelo closest() do TextWithNotes) */
.text-reading-area {
  flex: 1 0 auto;
  overflow: visible;
  padding: 6px 10px 24px 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  position: relative;
}

/* Canto superior direito da área de leitura. Fica acima do texto (z-index) e o
   `sticky` mantém os botões alcançáveis enquanto se rola um texto longo. */
.content-edit-actions {
  position: sticky;
  top: 4px;
  z-index: 6;
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
  /* Altura zero: não empurra o texto para baixo; os botões flutuam sobre ele. */
  height: 0;
}

/* ── Modo de edição: o editor é uma CAIXA, não uma camada sobre o texto ──────
   
   O desenho anterior sobrepunha: a faixa de Salvar/Cancelar e a barra do editor
   tinham altura zero no fluxo e flutuavam sobre o conteúdo, para entrar em edição
   não deslocar a leitura. O preço eram ~5rem de texto escondidos atrás delas.
   
   Agora o painel de metadados é ocultado durante a edição e o editor abre no
   topo, com rolagem própria e moldura. Com isso a sobreposição perde a razão de
   existir: não há mais conteúdo do leitor embaixo a preservar, e cada faixa passa
   a ocupar a altura que tem. */
.content-edit-actions--editing {
  top: 0;
  height: var(--content-edit-bar-h);
  /* Respiro entre a faixa e a moldura do editor. Sem ele a borda de baixo do
     botão Salvar encosta na borda de cima da barra de ferramentas, e as duas
     linhas viram um traço só. */
  margin-bottom: 0.5rem;
  align-items: center;
  background: var(--bg-main);
}

/* Altura da faixa de Salvar/Cancelar (botões de 2rem) mais um respiro. */
.text-reading-area {
  --content-edit-bar-h: 2.4rem;
}

/* Enquanto se edita, quem rola é o EDITOR, não a página.
   
   A cadeia precisa deixar a altura chegar até ele: o scroller externo para de
   rolar, e cada elo passa a poder encolher (`min-height: 0`, sem o qual um item
   flex se recusa a ficar menor que o conteúdo). No fim da cadeia o `.rte-content`
   já é `overflow-y: auto` por conta própria. */
.reader-scroll--editing {
  overflow: hidden;
}

.reader-scroll--editing .text-card {
  flex: 1 1 auto;
  min-height: 0;
}

.text-reading-area--editing {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.text-reading-area--editing :deep(.rte) {
  flex: 1 1 auto;
  min-height: 0;
}

/* Fundo SÓLIDO e literal, não `var(--bg-header)`: essa variável não é definida
   neste componente, e um botão que flutua sobre o texto precisa ser opaco — se o
   texto aparece por trás, o botão fica ilegível. */
.content-edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 2rem;
  padding: 0 0.6rem;
  border-radius: 0.4rem;
  border: 1px solid rgba(128, 128, 128, 0.35);
  background: var(--surface);
  color: var(--text-dark);
  font-size: 0.8rem;
  cursor: pointer;
  opacity: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.content-edit-btn:hover:not(:disabled) {
  background: #eef2f7;
}

/* Ao pressionar, só afunda: nada de transparência. */
.content-edit-btn:active:not(:disabled) {
  background: var(--neutral-200);
  opacity: 1;
}

.dark .content-edit-btn {
  border-color: rgba(255, 255, 255, 0.2);
}

.content-edit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Salvar o conteúdo: o mesmo roxo dos outros dois.
   O seletor soma as duas classes, e o hover e o tema escuro ganham regra
   própria: as regras de hover e de tema escuro do botão-base têm mais peso que
   uma classe sozinha, e repintariam este de branco sob o mouse e de
   cinza-escuro no tema escuro. */
.content-edit-btn.content-edit-btn--primary {
  border-color: transparent;
  background: var(--brand);
  color: var(--text-on-brand);
  font-weight: 600;
}

.content-edit-btn.content-edit-btn--primary:hover:not(:disabled),
.dark .content-edit-btn.content-edit-btn--primary:hover:not(:disabled) {
  background: var(--brand-hover);
}

.dark .content-edit-btn.content-edit-btn--primary {
  background: var(--brand);
  color: var(--text-on-brand);
}

/* Houve aqui um `.content-edit-btn--ghost`: copiar e editar sem moldura, com
   borda e fundo aparecendo só sob o ponteiro, para não competirem com o texto
   atrás deles. A troca foi deliberada — a moldura permanente pesa menos do que
   dois ícones soltos sobre a folha, que não se anunciam como botões e cruzam
   palavras sem nada que os separe.

   Sem a variante, os dois caem na base `.content-edit-btn`, que já traz borda,
   fundo opaco, sombra, hover, :active, :disabled e tema escuro. */

.content-edit-error {
  margin: 0.5rem 0;
  padding: 0.5rem 0.7rem;
  border-radius: 0.4rem;
  background: rgba(185, 28, 28, 0.1);
  color: var(--danger-text);
  font-size: 0.82rem;
}

/* ── A coluna de texto é a MESMA no leitor e no editor ──────────────────────
   O renderizador usa `.text-body`, com `max-width: 760px; margin: 0 auto` — a
   medida de leitura do Litterae. Sem repeti-la no editor, o texto ocupava a
   largura inteira e SALTAVA horizontalmente ao alternar entre ler e editar.

   A correção vai no editor, não no leitor: tirar o limite de largura da leitura
   deixaria linhas longuíssimas em tela larga, que é justamente o que a medida
   evita.

   `:deep()` é necessário porque `.rte-content` vive dentro do RichTextEditor, e
   este bloco é `scoped`. O padding lateral do editor é zerado: quem afasta das
   bordas agora é a mesma margem automática do leitor. */
/* Quem ROLA é a caixa inteira; quem tem 760px é a COLUNA DE TEXTO dentro dela.
   
   Estavam no mesmo elemento, e por isso a barra de rolagem aparecia grudada à
   direita do texto, no meio do editor, em vez de na borda interna da moldura: o
   elemento com `overflow-y: auto` era o próprio bloco de 760px centrado.
   
   O padding lateral é simétrico de propósito — ele reduz a largura disponível
   sem deslocar o centro, então a coluna continua alinhada com a do renderizador
   e o texto não encosta na moldura em tela estreita. */
.text-reading-area :deep(.rte-content) {
  max-width: none;
  margin: 0;
  padding: 0.85rem 0.75rem;
}

/* `width: 100%` antes do `max-width` dá ao item flexível uma largura definida no
   eixo transversal; sem ela, margem automática num container flex faz o bloco
   encolher até o conteúdo em vez de ocupar os 760px. */
.text-reading-area :deep(.rte-content > .ProseMirror) {
  width: 100%;
  max-width: 760px;
  margin-inline: auto;
}

/* ── Edição de texto puro: a caixa ocupa a área inteira ─────────────────────
   
   Duas correções, e cada uma resolve uma metade.
   
   ALTURA. A classe cai no INVÓLUCRO do CustomTextarea, não no <textarea>. O
   invólucro é uma coluna flexível, e o campo dentro dele tem `min-height: 200px`
   sem crescer — era daí que vinha a caixa de seis linhas com espaço vazio
   embaixo. Um `min-height` maior no invólucro não resolveria: aumentaria a caixa
   vazia, não o campo. Por isso o `flex` desce nos dois níveis.
   
   LARGURA. Sem o limite de 760px do leitor. A coluna de leitura existe para não
   se ler linha longa demais; editar texto puro é outra tarefa, e ali o que falta
   é espaço. O custo é o texto trocar de largura ao entrar em edição — diferente
   do ramo HTML, onde a coluna é preservada justamente para não saltar. */
.text-reading-area--editing .content-edit-textarea {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
}

/* `:deep()` porque o campo vive dentro do CustomTextarea e este bloco é scoped.
   `min-height: 0` anula o piso de 200px do componente, que aqui viraria um chão
   e impediria a caixa de acompanhar telas baixas. */
.text-reading-area--editing :deep(.custom-textarea) {
  flex: 1 1 auto;
  min-height: 0;
  resize: none;
}

.text-body {
  line-height: 1.55;
  font-size: 1.05rem;
  max-width: 760px;
  margin: 0 auto;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* O ⋯ de "Ações do texto" flutua no topo (faixa de altura zero) e a primeira
   linha corria por baixo dele. Só ficava livre quando o próprio conteúdo
   começava com uma linha em branco. O respiro é a altura do botão (28px) mais o
   `top` da faixa sticky, nos dois renderizadores. */
.text-body {
  padding-top: 32px;
}

</style>